<template>
  <div class="settlement-detail-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>结算单详情</span>
      </div>
      <div class="header-actions">
        <el-button v-permission="'settlement:settlement-detail:print'" size="small" type="success" icon="el-icon-printer" @click="printSettlement">打印结算单</el-button>
        <el-button size="small" type="primary" icon="el-icon-back" @click="goBack">返回列表</el-button>
      </div>
    </div>
    
    <!-- 加载中状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>
    
    <!-- 结算信息 -->
    <div v-else class="settlement-info">
      <!-- 结算单信息区域 -->
      <el-card class="box-card">
        <div slot="header" class="card-header">
          <span>结算单基本信息</span>
          <div class="total-amount">
            <span>结算总额：</span>
            <span class="amount-value">{{ formatAmount(settlement.amount || settlement.settlement_amount) }}</span>
          </div>
        </div>
        <el-descriptions border :column="3">
          <el-descriptions-item label="结算单号">{{ settlement.settlementNumber || settlement.settlement_no }}</el-descriptions-item>
          <el-descriptions-item label="结算标题">{{ settlement.settlementTitle || settlement.settlement_title }}</el-descriptions-item>
          <el-descriptions-item label="结算状态">
            <el-tag :type="getStatusTagType(settlement.status)">{{ getStatusText(settlement.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="需求方单位">{{ getDemandUnitsText() }}</el-descriptions-item>
          <el-descriptions-item label="供应商">{{ settlement.supplier?.unitName || settlement.supplier_name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="结算时间">{{ formatDateTime(settlement.settlementDate) }}</el-descriptions-item>
          <el-descriptions-item label="供应商确认时间">{{ formatDateTime(settlement.supplierConfirmationDate) }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDate(settlement.createTime || settlement.create_time) }}</el-descriptions-item>
          <!-- 采管费信息（当有采管费配置时显示） -->
          <el-descriptions-item v-if="hasProcurementManagementFee()" label="采管费类型">
            {{ getProcurementManagementFeeTypeText() }}
          </el-descriptions-item>
          <!-- 采管费模式（固定价或变动价时显示） -->
          <el-descriptions-item v-if="(isFixedPriceProcurementManagementFee() || isFloatingPriceProcurementManagementFee()) && settlement.procurementManagementFeeMode" label="采管费模式">
            {{ getProcurementManagementFeeModeText() }}
          </el-descriptions-item>
          <el-descriptions-item v-if="hasProcurementManagementFee() && settlement.procurementManagementFeeType === 'FIXED_RATE'" label="采管费（%）">
            {{ formatProcurementManagementFeeRate() }}
          </el-descriptions-item>
          <el-descriptions-item v-if="hasProcurementManagementFee() && settlement.procurementManagementFeeType === 'FIXED_UNIT_PRICE'" 
            :label="(isFixedPriceProcurementManagementFee() || isFloatingPriceProcurementManagementFee()) && settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT' ? '采管费金额' : '采管费单价'">
            {{ formatUnitPrice(settlement.procurementManagementFeeUnitPrice || 0) }} 元
          </el-descriptions-item>
          <el-descriptions-item label="备注" :span="3">{{ settlement.remark || '无' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>
      
      <!-- 可折叠区域 -->
      <el-collapse v-model="activeCollapse" accordion>
        <!-- 结算清单信息折叠面板 -->
        <el-collapse-item title="结算清单信息" name="settlement-items">
          <div class="settlement-items-section">
            <!-- 结算清单信息 -->
            <el-card class="box-card settlement-items-info" v-if="settlement.settlementItems && settlement.settlementItems.length > 0">
              <div slot="header" class="card-header">
                <span>结算清单明细 (共{{ settlement.settlementItems.length }}项)</span>
                <div class="total-amount">
                  <span>结算总额：</span>
                <!-- 结算单主金额包含采管费，与列表 settlements.amount 保持一致 -->
                <span class="amount-value">{{ formatAmount(settlement.amount !== undefined && settlement.amount !== null ? settlement.amount : calculateSettlementItemsTotal()) }}</span>
                </div>
              </div>
              
              <!-- 结算项目表格 -->
              <el-table 
                :data="settlement.settlementItems" 
                style="width: 100%" 
                border
                show-summary
                :summary-method="getTableSummary">
                <!-- 序号 -->
                <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
                
                <!-- 订单编号 -->
                <el-table-column label="订单编号" width="120">
                  <template slot-scope="scope">
                    {{ scope.row.orderNumber || scope.row.order?.order_id || '-' }}
                  </template>
                </el-table-column>
                
                <!-- 收货日期 -->
                <el-table-column label="收货日期" width="120">
                  <template slot-scope="scope">
                    {{ formatDate(scope.row.receivedTime) }}
                  </template>
                </el-table-column>
                
                <!-- 材料名称 -->
                <el-table-column label="材料名称" min-width="120">
                  <template slot-scope="scope">
                    {{ scope.row.materialsName || scope.row.itemProductName || scope.row.product_name || '-' }}
                  </template>
                </el-table-column>
                
                <!-- 规格型号 -->
                <el-table-column label="规格型号" min-width="150">
                  <template slot-scope="scope">
                    <el-tooltip
                      v-if="getSpecificationText(scope.row)"
                      :content="getSpecificationText(scope.row)"
                      placement="top"
                      effect="dark"
                      popper-class="spec-tooltip"
                    >
                      <span>{{ truncateText(getSpecificationText(scope.row), 20) }}</span>
                    </el-tooltip>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                
                <!-- 收货数量 -->
                <el-table-column label="收货数量" width="120">
                  <template slot-scope="scope">
                    {{ scope.row.settledQuantity || scope.row.settlement_quantity || scope.row.receiptQuantity || '-' }}
                  </template>
                </el-table-column>
                
                <!-- 单位 -->
                <el-table-column label="单位" width="80">
                  <template slot-scope="scope">
                    {{ scope.row.unit || scope.row.itemUnit || scope.row.product_unit || '-' }}
                  </template>
                </el-table-column>
                
                <!-- 含税固定价（仅变动价产品显示） -->
                <el-table-column v-if="hasFloatingPriceInSettlement()" label="含税固定价" width="100">
                  <template slot-scope="scope">
                    {{ formatUnitPrice(scope.row.itemTaxInclusiveBasePrice || 0) }}
                  </template>
                </el-table-column>
                
                <!-- 变动价（仅变动价产品显示） -->
                <el-table-column v-if="hasFloatingPriceInSettlement()" label="变动价" width="100">
                  <template slot-scope="scope">
                    {{ formatUnitPrice(scope.row.itemTaxInclusiveFloatingPrice || 0) }}
                  </template>
                </el-table-column>
                
                <!-- 基础单价（仅变动价产品显示：基础价+变动价） -->
                <el-table-column v-if="hasFloatingPriceInSettlement()" label="基础单价" width="100">
                  <template slot-scope="scope">
                    {{ formatUnitPrice(calculateBaseUnitPrice(scope.row)) }}
                  </template>
                </el-table-column>
                
                <!-- 含税单价（仅固定价产品显示） -->
                <el-table-column v-if="!hasFloatingPriceInSettlement()" label="含税单价" width="100">
                  <template slot-scope="scope">
                    {{ formatUnitPrice(scope.row.itemTaxInclusiveUnitPrice || scope.row.unitPrice || scope.row.product_price) }}
                  </template>
                </el-table-column>
                
                <!-- 采管费单价（仅当清单中存在采管费/采管费金额不为0时显示） -->
                <el-table-column v-if="showProcurementManagementFeeColumns()" label="采管费单价" width="120">
                  <template slot-scope="scope">
                    {{ formatProcurementFeeUnitPrice(calculateProcurementManagementFeeUnitPrice(scope.row)) }}
                  </template>
                </el-table-column>
                
                <!-- 采管费金额（仅当清单中存在采管费/采管费金额不为0时显示） -->
                <el-table-column v-if="showProcurementManagementFeeColumns()" label="采管费金额" width="120">
                  <template slot-scope="scope">
                    {{ formatAmount(scope.row.procurementManagementFee || 0) }}
                  </template>
                </el-table-column>
                
                <!-- 结算单价（变动价产品：基础单价+采管费单价；固定价产品不显示此列） -->
                <el-table-column v-if="hasFloatingPriceInSettlement()" label="结算单价" width="100">
                  <template slot-scope="scope">
                    {{ formatUnitPrice(calculateSettlementUnitPrice(scope.row)) }}
                  </template>
                </el-table-column>
                
                <!-- 实际单价列（只在固定价产品且采管费不为0时显示） -->
                <el-table-column v-if="!hasFloatingPriceInSettlement() && hasProcurementManagementFee()" label="实际单价" width="100">
                  <template slot-scope="scope">
                    {{ formatUnitPrice(calculateActualUnitPrice(scope.row)) }}
                  </template>
                </el-table-column>
                
                <!-- 含税金额 -->
                <el-table-column label="含税金额" width="120">
                  <template slot-scope="scope">
                    {{ formatAmount(calculateSettlementItemInclusiveAmount(scope.row)) }}
                  </template>
                </el-table-column>
                
                <!-- 税率 -->
                <el-table-column label="税率" width="80">
                  <template slot-scope="scope">
                    {{ ((scope.row.taxRate || scope.row.itemTaxRate || scope.row.product_tax_rate || 0) * 100).toFixed(1) }}%
                  </template>
                </el-table-column>
                
                <!-- 税额 -->
                <el-table-column label="税额" width="120">
                  <template slot-scope="scope">
                    {{ formatAmount(calculateItemTaxAmount(scope.row)) }}
                  </template>
                </el-table-column>
                
                <!-- 不含税金额 -->
                <el-table-column label="不含税金额" width="120">
                  <template slot-scope="scope">
                    {{ formatAmount(calculateTaxExclusiveAmount(scope.row)) }}
                  </template>
                </el-table-column>

              </el-table>
              
              <!-- 底部汇总行 -->
              <div class="settlement-items-summary">
                <div class="summary-item">
                  <span class="summary-label">项目总数：</span>
                  <span class="summary-value">{{ settlement.settlementItems.length }} 项</span>
                </div>
                <!-- 固定价或变动价合计模式：显示含税金额小计 -->
                <div v-if="(isFixedPriceProcurementManagementFee() || isFloatingPriceProcurementManagementFee()) && settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT'" class="summary-item highlight">
                  <span class="summary-label">含税金额小计：</span>
                  <span class="summary-value">{{ formatAmount(calculateSettlementItemsTotal()) }}</span>
                </div>
                <!-- 其他情况：显示含税金额合计 -->
                <div v-else class="summary-item highlight">
                  <span class="summary-label">含税金额合计：</span>
                  <span class="summary-value">{{ formatAmount(calculateSettlementItemsTotal()) }}</span>
                </div>
                <!-- 采管费总额（合计模式取结算单配置，单价模式取明细合计） -->
                <div v-if="calculateSettlementProcurementManagementFeeTotal() !== 0" class="summary-item highlight">
                  <span class="summary-label">采管费总额：</span>
                  <span class="summary-value">{{ formatAmount(calculateSettlementProcurementManagementFeeTotal()) }}</span>
                </div>
                <!-- 含税金额小计 + 采管费总额 -->
                <div v-if="calculateSettlementProcurementManagementFeeTotal() !== 0" class="summary-item highlight">
                  <span class="summary-label">合计：</span>
                  <span class="summary-value">{{ formatAmount(calculateSettlementItemsTotal() + calculateSettlementProcurementManagementFeeTotal()) }}</span>
                </div>
              </div>
            </el-card>
            
            <!-- 无结算清单时显示 -->
            <div v-else class="empty-settlement-items">
              <el-alert
                title="没有找到结算清单数据"
                type="info"
                :closable="false"
                show-icon>
              </el-alert>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
      
      <!-- 操作按钮 -->
      <div class="action-buttons hide-on-print">
        <!-- 草稿状态下的按钮（供应商审核） -->
        <template v-if="settlement.status === 'DRAFT'">
          <!-- 供应商审核按钮 - 只有供应商审核人时显示 -->
          <el-button 
            v-if="isSupplierAuditor && settlement.needSupplierAudit"
            type="primary" 
            @click="supplierAuditSettlement"
            :loading="supplierAuditLoading"
          >供应商审核</el-button>
        </template>
        
        <!-- 待确认状态下的按钮 -->
        <template v-if="settlement.status === 'ACTIVE'">
          <!-- 需求方确认按钮 - 只有需求方且不是审核人时显示 -->
          <el-button 
            v-if="isDemandUnit && !isAuditor"
            type="success" 
            v-permission="'settlement:settlement-detail:confirm'"
            @click="confirmSettlement"
            :loading="confirmLoading"
          >确认结算</el-button>
          
          <!-- 审核人审核按钮 - 只有审核人时显示 -->
          <el-button 
            v-if="isAuditor"
            type="primary" 
            @click="auditSettlement"
            :loading="auditLoading"
          >审核结算</el-button>
        </template>
        
        <!-- 已确认状态下的按钮 -->
        <template v-if="settlement.status === 'EXECUTING'">
          <!-- 创建人回退按钮 - 已开票状态不允许回退 -->
          <el-button 
            v-if="isCreator && settlement.status !== 'INVOICED'"
            type="warning" 
            @click="rollbackSettlement"
            :loading="rollbackLoading"
          >回退状态</el-button>
        </template>
        
        <!-- 已开票状态下的按钮 -->
        <template v-if="settlement.status === 'INVOICED'">
          <!-- 可以添加其他操作按钮，比如完成结算等 -->
          <el-button 
            v-if="isCreator"
            type="success" 
            @click="finishSettlement"
            :loading="finishLoading"
          >完成结算</el-button>
        </template>
        
        <!-- <el-button v-if="['ACTIVE', 'EXECUTING', 'INVOICED'].includes(settlement.status)" type="danger" @click="cancelSettlement">撤销结算</el-button> -->
      </div>
    </div>
    
    <!-- 打印专用区域 - 默认隐藏，只在打印时显示 -->
    <div id="printArea" class="print-only-area">
      <!-- 标题 -->
      <div class="print-title">对账结算单</div>
      
      <!-- 结算期 -->
      <table class="print-period-table">
        <tr>
          <td class="period-label">结算期</td>
          <td class="period-value" colspan="3">{{ formatSettlementPeriod() }}</td>
        </tr>
      </table>
      
      <!-- 单位信息 -->
      <table class="print-info-table">
        <tr>
          <td class="info-label">需求单位</td>
          <td class="info-value">{{ getDemandUnitsText() }}</td>
          <td class="info-label">供货单位</td>
          <td class="info-value">{{ settlement.supplier?.unitName || settlement.supplier_name || '-' }}</td>
        </tr>
        <tr>
          <td class="info-label">二级机构</td>
          <td class="info-value">{{ settlement.project?.name || settlement.project_name || '-' }}</td>
          <td class="info-label">结算单编号</td>
          <td class="info-value">{{ settlement.settlementNumber || settlement.settlement_no }}</td>
        </tr>
      </table>
      
      <!-- 材料明细表格 - 根据定价方式显示不同列 -->
      <table class="print-materials-table">
        <thead>
          <tr>
            <th class="col-serial">序号</th>
            <th class="col-order">订单编号</th>
            <th class="col-date">收货日期</th>
            <th class="col-name">材料名称</th>
            <th class="col-spec">规格型号</th>
            <th class="col-quantity">收货数量</th>
            <th class="col-unit">单位</th>
            <!-- 变动价产品显示含税固定价、变动价、基础单价 -->
            <th v-if="hasFloatingPriceInSettlement()" class="col-base-price">含税固定价</th>
            <th v-if="hasFloatingPriceInSettlement()" class="col-floating-price">变动价</th>
            <th v-if="hasFloatingPriceInSettlement()" class="col-price">基础单价</th>
            <!-- 固定价产品显示含税单价 -->
            <th v-if="!hasFloatingPriceInSettlement()" class="col-price">含税单价</th>
            <!-- 采管费列（仅当清单中存在采管费/采管费金额不为0时显示） -->
            <th v-if="showProcurementManagementFeeColumns()" class="col-price">采管费单价</th>
            <th v-if="showProcurementManagementFeeColumns()" class="col-amount">采管费金额</th>
            <!-- 变动价产品显示结算单价 -->
            <th v-if="hasFloatingPriceInSettlement()" class="col-price">结算单价</th>
            <th class="col-amount">含税金额</th>
            <th class="col-tax-rate">税率</th>
            <th class="col-tax-amount">税额</th>
            <th class="col-amount">不含税金额</th>
          </tr>
        </thead>
        <tbody>
          <!-- 动态材料明细数据 -->
          <tr v-for="(item, index) in getPrintMaterialsList()" :key="index">
            <td class="col-serial">{{ index + 1 }}</td>
            <td class="col-order">{{ item.orderNumber || item.order?.order_id || '-' }}</td>
            <td class="col-date">{{ formatDate(item.receivedTime) }}</td>
            <td class="col-name">{{ item.materialsName || item.itemProductName || item.product_name || '-' }}</td>
            <td class="col-spec">
              <span>{{ truncateText(getSpecificationText(item), 15) || '-' }}</span>
            </td>
            <td class="col-quantity">{{ item.settledQuantity || item.settlement_quantity || item.receiptQuantity || '-' }}</td>
            <td class="col-unit">{{ item.unit || item.itemUnit || item.product_unit || '-' }}</td>
            <!-- 变动价产品显示基础价、变动价、基础单价 -->
            <td v-if="hasFloatingPriceInSettlement()" class="col-base-price">
              {{ formatUnitPrice(item.itemTaxInclusiveBasePrice || 0) }}
            </td>
            <td v-if="hasFloatingPriceInSettlement()" class="col-floating-price">
              {{ formatUnitPrice(item.itemTaxInclusiveFloatingPrice || 0) }}
            </td>
            <td v-if="hasFloatingPriceInSettlement()" class="col-price">
              {{ formatUnitPrice(calculateBaseUnitPrice(item)) }}
            </td>
            <!-- 固定价产品显示含税单价 -->
            <td v-if="!hasFloatingPriceInSettlement()" class="col-price">
              {{ formatUnitPrice(item.itemTaxInclusiveUnitPrice || item.unitPrice || item.product_price) }}
            </td>
            <!-- 采管费列（仅当清单中存在采管费/采管费金额不为0时显示） -->
            <td v-if="showProcurementManagementFeeColumns()" class="col-price">
              {{ formatProcurementFeeUnitPrice(calculateProcurementManagementFeeUnitPrice(item)) }}
            </td>
            <td v-if="showProcurementManagementFeeColumns()" class="col-amount">
              {{ formatAmount(item.procurementManagementFee || 0) }}
            </td>
            <!-- 变动价产品显示结算单价 -->
            <td v-if="hasFloatingPriceInSettlement()" class="col-price">
              {{ formatUnitPrice(calculateSettlementUnitPrice(item)) }}
            </td>
            <td class="col-amount">{{ formatAmount(item.settledAmount || item.price_total) }}</td>
            <td class="col-tax-rate">{{ ((item.taxRate || item.itemTaxRate || item.product_tax_rate || 0) * 100).toFixed(1) }}%</td>
            <td class="col-tax-amount">{{ formatAmount(calculateItemTaxAmount(item)) }}</td>
            <td class="col-amount">{{ formatAmount(calculateTaxExclusiveAmount(item)) }}</td>
          </tr>
          <!-- 合计行 -->
          <tr class="total-row">
            <td class="col-serial">{{ getPrintMaterialsList().length + 1 }}</td>
            <td class="col-order" colspan="6">合计</td>
            <!-- 变动价：基础价+变动价+基础单价=3列 -->
            <td v-if="hasFloatingPriceInSettlement()" class="col-base-price" colspan="3"></td>
            <!-- 固定价：含税单价=1列 -->
            <td v-if="!hasFloatingPriceInSettlement()" class="col-price"></td>
            <!-- 采管费：采管费单价+采管费金额=2列 -->
            <td v-if="showProcurementManagementFeeColumns()" class="col-price" colspan="2"></td>
            <!-- 变动价：结算单价=1列 -->
            <td v-if="hasFloatingPriceInSettlement()" class="col-price"></td>
            <td class="col-amount">{{ formatAmount(calculateSettlementItemsTotal()) }}</td>
            <td class="col-tax-rate"></td>
            <td class="col-tax-amount">{{ formatAmount(calculateSettlementItemsTaxTotal()) }}</td>
            <td class="col-amount">{{ formatAmount(calculateSettlementItemsTaxExclusiveTotal()) }}</td>
          </tr>
        </tbody>
      </table>
      
      <!-- 签名区域（仅当状态为已确认/EXECUTING 时显示需求方签名） -->
      <table class="print-signature-table">
        <tr>
          <td class="signature-cell">
            <span class="signature-label">需求单位经办人：</span>
            <span v-if="settlement.status === 'EXECUTING'"></span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">时间</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">供应单位经办人：</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">时间</span>
          </td>
        </tr>
        <tr>
          <td class="signature-cell">
            <span class="signature-label">需求单位审核人：</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">时间</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">供应单位审核人：</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">时间</span>
          </td>
        </tr>
      </table>
    </div>
    
    <!-- 确认结算对话框 -->
    <el-dialog v-dialog-drag title="确认结算" :visible.sync="confirmDialogVisible" width="500px" :close-on-click-modal="false">
      <el-form :model="confirmForm" :rules="confirmFormRules" ref="confirmForm" label-width="120px">
        <el-form-item label="是否需要审核" prop="needAudit">
          <el-radio-group v-model="confirmForm.needAudit">
            <el-radio :label="true">需要审核</el-radio>
            <el-radio :label="false">不需要审核</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="confirmForm.needAudit" label="选择审核人" prop="auditorId">
          <el-select 
            v-model="confirmForm.auditorId" 
            placeholder="请选择审核人" 
            style="width: 100%" 
            filterable
            :loading="auditorListLoading"
            loading-text="正在查询审核人..."
          >
            <el-option
              v-for="user in auditorList"
              :key="user.id"
              :label="`${user.realName || user.username} (${user.unitName || ''})`"
              :value="user.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="确认备注">
          <el-input type="textarea" v-model="confirmForm.remark" rows="3" placeholder="请输入确认备注（可选）"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="confirmDialogVisible = false">取 消</el-button>
        <el-button v-permission="'settlement:settlement-detail:confirm'" type="primary" @click="submitConfirmForm" :loading="confirmLoading">确认结算</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  query_settlement_by_id,
  confirm_settlement,
  audit_settlement,
  rollback_settlement,
  finish_settlement,
  supplier_audit_settlement
} from '@/api/modules/settlement';
import { 
  getUsersByUnitId,
  queryUsersPagination
} from '@/api/modules/users';
import {
  getFixedPriceSettlementPrintData,
  getFloatingPriceSettlementPrintData
} from '@/api/modules/print';
import { getUserInfo } from '@/utils/auth';
import { getApiUrl } from '@/utils/env';
import { installPlatformPrintFooter } from '@/utils/print-footer';

export default {
  name: 'SettlementDetail',
  data() {
    return {
      loading: true,
      settlementId: null,
      // 当前用户单位ID
      currentUnitId: null,
      // 折叠面板激活项
      activeCollapse: [],
      settlement: {
        id: '',
        settlement_no: '',
        settlement_title: '',
        settlement_amount: '',
        period_start: '',
        period_end: '',
        status: '',
        createTime: '',
        remark: ''
      },
      // 订单信息
      orderInfo: {
        id: '',
        order_id: '',
        order_title: '',
        status: '',
        supplier_id: '',
        contract_id: ''
      },
      // 订单明细
      orderItems: [],
      // 订单详情
      orderDetail: null,

      // 订单明细总金额
      orderItemsTotalAmount: 0,
      // 订单明细总数量
      orderItemsTotalQuantity: 0,
      // 订单明细总税费
      orderItemsTotalTax: 0,
      confirmLoading: false,
      
      // 确认结算相关
      confirmDialogVisible: false,
      confirmForm: {
        needAudit: false,
        auditorId: null,
        remark: ''
      },
      confirmFormRules: {
        needAudit: [
          { required: true, message: '请选择是否需要审核', trigger: 'change' }
        ],
        auditorId: [
          { 
            validator: (rule, value, callback) => {
              if (this.confirmForm.needAudit && !value) {
                callback(new Error('请选择审核人'));
              } else {
                callback();
              }
            }, 
            trigger: 'change' 
          }
        ]
      },
      
      // 审核相关
      auditLoading: false,
      
      // 回退相关
      rollbackLoading: false,
      
      // 完成结算相关
      finishLoading: false,
      
      // 供应商审核相关
      supplierAuditLoading: false,
      
      // 审核人列表
      auditorList: [],
      
      // 审核人列表加载状态
      auditorListLoading: false,
      
      // 当前用户信息
      currentUser: null
    };
  },
  computed: {
    // 检查当前用户是否是供应商
    isSupplier() {
      if (!this.settlement || !this.currentUnitId) {
        return false;
      }
      
      // 检查当前用户单位ID是否与结算单中的供应商ID匹配
      const supplierId = this.settlement.supplier?.id || this.settlement.supplier_id;
      return supplierId === this.currentUnitId || supplierId == this.currentUnitId;
    },
    
    // 检查当前用户是否是需求方单位
    isDemandUnit() {
      if (!this.settlement || !this.currentUnitId) {
return false;
      }
      
      // 首先检查 demandUnitIds 数组（这是主要的判断方式）
      if (this.settlement.demandUnitIds && Array.isArray(this.settlement.demandUnitIds)) {
        const isInDemandUnitIds = this.settlement.demandUnitIds.includes(this.currentUnitId);
if (isInDemandUnitIds) {
          return true;
        }
      }
      
      // 检查当前用户单位ID是否与结算单中的需求方ID匹配
      const demandId = this.settlement.demand?.id || this.settlement.demand_id || this.settlement.demandUnitId;
if (demandId) {
        const isMatch = demandId === this.currentUnitId || demandId == this.currentUnitId;
return isMatch;
      }
      
      // 如果没有直接的需求方ID，检查结算订单中的target_id
      if (this.settlement.settlement_orders && Array.isArray(this.settlement.settlement_orders)) {
const isTargetMatch = this.settlement.settlement_orders.some(order => {
          const isMatch = order.target_id === this.currentUnitId;
return isMatch;
        });
return isTargetMatch;
      }
      
return false;
    },
    
    // 检查当前用户是否是结算对象（保留兼容性）
    isCurrentUserTarget() {
      return this.isDemandUnit();
    },
    
    // 检查当前用户是否是审核人
    isAuditor() {
      if (!this.settlement || !this.currentUser) {
        return false;
      }
      
      const auditorId = this.settlement.auditorId || this.settlement.auditor_id;
      const currentUserId = this.currentUser.id || this.currentUser.userId;
      
return auditorId && (auditorId === currentUserId || auditorId == currentUserId);
    },
    
    // 检查当前用户是否是结算单创建人
    isCreator() {
      if (!this.settlement || !this.currentUser) {
        return false;
      }
      
      const creatorId = this.settlement.creatorId || this.settlement.creator_id || this.settlement.createId || this.settlement.create_id;
      const currentUserId = this.currentUser.id || this.currentUser.userId;
      
return creatorId && (creatorId === currentUserId || creatorId == currentUserId);
    },
    
    // 检查当前用户是否是供应商审核人
    isSupplierAuditor() {
      if (!this.settlement || !this.currentUser) {
        return false;
      }
      
      const supplierAuditorId = this.settlement.supplierAuditorId || this.settlement.supplier_auditor_id;
      const currentUserId = this.currentUser.id || this.currentUser.userId;
      
return supplierAuditorId && (supplierAuditorId === currentUserId || supplierAuditorId == currentUserId);
    }
  },
  watch: {
    // 监听是否需要审核的选择变化
    'confirmForm.needAudit'(newVal) {
      if (newVal === true) {
        // 当选择需要审核时，查询审核人列表
        this.getAuditorList();
      } else {
        // 当选择不需要审核时，清空审核人选择
        this.confirmForm.auditorId = null;
        this.auditorList = [];
      }
    }
  },
  created() {
    // 获取路由参数中的结算单ID
    this.settlementId = this.$route.params.id;
    // 获取当前用户单位ID
    this.getCurrentUnitId();
    // 获取当前用户信息
    this.getCurrentUserInfo();
    // 加载结算单详情
    this.getSettlementDetail();
  },
  methods: {
    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        // 添加防御性检查，避免访问不存在的属性
        const store = this.$store || {};
        const state = store.state || {};
        
        // 尝试从不同可能的路径获取用户信息
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          // 如果是 state.user.userInfo 的结构
          userInfo = state.user.userInfo;
} else if (state.userInfo) {
          // 如果是 state.userInfo 的结构
          userInfo = state.userInfo;
} else if (state.auth && state.auth.userInfo) {
          // 如果是 state.auth.userInfo 的结构
          userInfo = state.auth.userInfo;
}
        
        // 如果找不到，可以考虑从localStorage获取
        if (!userInfo || !userInfo.unitId) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
} catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
if (userInfo && userInfo.unitId) {
          this.currentUnitId = userInfo.unitId;
} else {
          console.warn('未能获取unitId，将使用默认值1进行测试');
          // 使用默认值便于测试
          this.currentUnitId = 1;
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err);
        console.warn('发生错误，将使用默认值1进行测试');
        // 出错时使用默认值便于测试
        this.currentUnitId = 1;
      }
    },
    
    // 获取当前用户信息
    getCurrentUserInfo() {
      try {
        // 使用auth.js中的getUserInfo函数
        const userInfo = getUserInfo();
if (userInfo) {
          this.currentUser = userInfo;
} else {
          console.warn('未能获取用户信息');
          this.$message.error('未能获取用户信息，请重新登录');
        }
      } catch (err) {
        console.error('获取用户信息出错:', err);
        this.$message.error('获取用户信息失败');
      }
    },
    
    // 获取审核人列表（逻辑与 create-settlement 的 getSupplierAuditorList 一致：查询当前登录人同单位的其他用户）
    async getAuditorList() {
      try {
        this.auditorListLoading = true;
        
        // 多来源获取用户信息（与 interest-calculation-detail 一致）
        let userInfo = this.currentUser || getUserInfo();
        if (!userInfo || !userInfo.unitId) {
          const store = this.$store?.state || {};
          userInfo = store.user?.userInfo || store.userInfo || store.auth?.userInfo || userInfo;
        }
        if (!userInfo || !(userInfo.unitId ?? userInfo.unit_id)) {
          try {
            const local = localStorage.getItem('userInfo') || localStorage.getItem('user_info');
            if (local) userInfo = JSON.parse(local);
          } catch (e) { /* ignore */ }
        }
        
        if (!userInfo) {
          console.warn('当前用户信息不存在，无法获取审核人列表');
          this.$message.error('当前用户信息不存在，无法获取审核人列表');
          return;
        }
        
        // 多来源获取 roleId，并转为数字以兼容字符串类型（如 "2"、"3"）
        const roleIdRaw = userInfo.role?.id ?? userInfo.roleId ?? userInfo.role_id ?? userInfo.roles?.[0]?.id;
        const roleId = roleIdRaw != null ? Number(roleIdRaw) : null;
        const unitId = userInfo.unitId ?? userInfo.unit_id ?? this.currentUnitId;
        const projectIds = userInfo.projectIds ?? userInfo.project_ids ?? (Array.isArray(userInfo.projectId) ? userInfo.projectId : userInfo.projectId ? [userInfo.projectId] : []);
        const currentUserId = userInfo.id ?? userInfo.userId ?? userInfo.user_id;
        
if (roleId === 2 && unitId) {
          // 单位用户，获取本单位其他用户
const response = await getUsersByUnitId(unitId);
if (response.data && response.data.success) {
            const allUsers = response.data.data || [];
            
            this.auditorList = allUsers.filter(user => (user.id ?? user.userId) !== currentUserId);
            if (this.auditorList.length === 0) {
              this.$message.warning('本单位没有其他用户可以作为审核人');
            }
          } else {
            console.error('getUsersByUnitId接口调用失败:', response.data);
            this.$message.error('获取单位用户列表失败');
          }
        } else if (roleId === 3 && projectIds && projectIds.length > 0) {
          // 项目部用户，获取同项目用户
          const projectIdsStr = Array.isArray(projectIds) ? projectIds.join(',') : String(projectIds);
          const response = await queryUsersPagination({
            projectIds: projectIdsStr,
            page: 0,
            size: 100
          });
          
          if (response.data && response.data.success) {
            const allUsers = response.data.data?.content || [];
            this.auditorList = allUsers.filter(user => (user.id ?? user.userId) !== currentUserId);
            if (this.auditorList.length === 0) {
              this.$message.warning('同项目没有其他用户可以作为审核人');
            }
          } else {
            console.error('queryUsersPagination接口调用失败:', response.data);
            this.$message.error('获取项目用户列表失败');
          }
        } else if (unitId && (roleId == null || roleId === 1)) {
          // 管理员(roleId=1)或角色未知但有 unitId 时，按单位查询（需求方确认时多为单位用户场景）
          const response = await getUsersByUnitId(unitId);
          if (response.data && response.data.success) {
            const allUsers = response.data.data || [];
            this.auditorList = allUsers.filter(user => (user.id ?? user.userId) !== currentUserId);
            if (this.auditorList.length === 0) {
              this.$message.warning('本单位没有其他用户可以作为审核人');
            }
          } else {
            this.$message.error('获取单位用户列表失败');
          }
        } else {
          console.warn('未知的用户角色:', roleId);
          this.$message.warning('未知的用户角色，无法获取审核人列表');
        }
      } catch (error) {
        console.error('获取审核人列表失败:', error);
        this.$message.error('获取审核人列表失败: ' + error.message);
      } finally {
        this.auditorListLoading = false;
      }
    },
    
    // 获取需求方单位名称文本（只显示第一个）
    getDemandUnitsText() {
      if (!this.settlement || !this.settlement.demandUnits) {
        return '-';
      }
      
      // 从demandUnits数组中获取单位名称
      const unitNames = this.settlement.demandUnits.map(unit => unit.name || unit.unitName).filter(name => name);
      
      if (unitNames.length === 0) {
        return '-';
      }
      
      // 只返回第一个需求方单位名称
      return unitNames[0];
    },
    
    // 获取当前用户单位名称（保留作为备用方法）
    getCurrentUnitName() {
      try {
        const store = this.$store || {};
        const state = store.state || {};
        
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          userInfo = state.user.userInfo;
        } else if (state.userInfo) {
          userInfo = state.userInfo;
        } else if (state.auth && state.auth.userInfo) {
          userInfo = state.auth.userInfo;
        }
        
        if (!userInfo) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
            } catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
        if (userInfo && userInfo.unitName) {
          return userInfo.unitName;
        } else if (userInfo && userInfo.unit_name) {
          return userInfo.unit_name;
        } else {
          return '当前单位';
        }
      } catch (err) {
        console.error('获取用户单位名称出错:', err);
        return '当前单位';
      }
    },
    
    // 获取结算单详情
    getSettlementDetail() {
      this.loading = true;
      
      // 调用API获取结算单详情
      query_settlement_by_id(this.settlementId)
        .then(response => {
          if (response.data && response.data.success) {
// 获取结算单数据
            const data = response.data.data || {};
            
            // 更新结算单信息
            this.settlement = {
              ...this.settlement,
              ...data
            };
            
            // 处理订单信息 - 新的API返回settlement_orders数组
            if (data.settlement_orders && Array.isArray(data.settlement_orders) && data.settlement_orders.length > 0) {
              // 提取订单信息
              const orders = data.settlement_orders.map(item => item.order);
              
              // 先用第一个订单的信息填充订单信息区域
              const firstOrder = orders[0];
              this.orderInfo = {
                ...firstOrder,
                id: firstOrder.id,
                order_id: firstOrder.order_id,
                order_title: firstOrder.order_title,
                price_total: firstOrder.price_total,
                status: firstOrder.status,
                supplier_id: firstOrder.supplier_id,
                create_id: firstOrder.create_id,
                contract_id: firstOrder.contract_id
              };
              
              // 保存结算订单关联信息（包含完整的订单信息，包括联系人信息）
              this.orderDetail = {
                ...firstOrder,
                // 订单数量
                orders_count: orders.length,
                // 存储完整的结算订单列表
                all_orders: data.settlement_orders,
                // 存储单位信息
                unit_info: firstOrder.unit_info
              };
            } else if (data.order) {
              // 兼容旧API格式
              this.orderInfo = data.order;
              this.orderDetail = data.order;
            }
            
            // 处理产品明细 - 从settlement_products获取
            if (data.settlement_products && Array.isArray(data.settlement_products)) {
              this.orderItems = data.settlement_products;
              
              // 调试：检查税率数据
this.calculateOrderItemsSummary(data.settlement_products);
            }
            
          } else {
            this.$message.error(response.data?.message || '获取结算单详情失败');
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('获取结算单详情失败:', error);
          this.$message.error('获取结算单详情失败，请稍后重试');
          this.loading = false;
        });
    },
    
    // 打印结算单
    async printSettlement() {
      try {
        // 显示加载状态
        const loadingInstance = this.$loading({
          lock: true,
          text: '正在获取打印数据...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });

        // 判断是固定价还是变动价，调用不同的API
        const hasFloatingPrice = this.hasFloatingPriceInSettlement();
        const response = hasFloatingPrice 
          ? await getFloatingPriceSettlementPrintData(this.settlementId)
          : await getFixedPriceSettlementPrintData(this.settlementId);
        
        if (!response.data || !response.data.success) {
          throw new Error(response.data?.message || '获取打印数据失败');
        }
        
        const printData = response.data.data;
// 使用打印 API 返回的签名图片 URL（参见 PRINT_API.md）
        // demandUnitSignatureUrl / supplierOperatorSignatureUrl / demandAuditorSignatureUrl / supplierAuditorSignatureUrl
        const demandUnitSignatureUrl = printData?.demandUnitSignatureUrl
          ? this.buildSignatureImageUrl(printData.demandUnitSignatureUrl)
          : null;
        const supplierOperatorSignatureUrl = printData?.supplierOperatorSignatureUrl
          ? this.buildSignatureImageUrl(printData.supplierOperatorSignatureUrl)
          : null;
        const demandAuditorSignatureUrl = printData?.demandAuditorSignatureUrl
          ? this.buildSignatureImageUrl(printData.demandAuditorSignatureUrl)
          : null;
        const supplierAuditorSignatureUrl = printData?.supplierAuditorSignatureUrl
          ? this.buildSignatureImageUrl(printData.supplierAuditorSignatureUrl)
          : null;
        
        // 生成打印内容（传递经办人、审核人签名图片URL）
        const printContent = hasFloatingPrice
          ? this.generateFloatingPricePrintContent(printData, demandUnitSignatureUrl, supplierOperatorSignatureUrl, demandAuditorSignatureUrl, supplierAuditorSignatureUrl)
          : this.generatePrintContent(printData, demandUnitSignatureUrl, supplierOperatorSignatureUrl, demandAuditorSignatureUrl, supplierAuditorSignatureUrl);
        
        // 创建新的打印窗口
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        
        // 写入打印内容
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>对账结算单</title>
              <meta charset="utf-8">
              <style>
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                
                body {
                  font-family: "SimSun", "宋体", serif;
                  background: white;
                  padding: 15mm;
                  font-size: 12px;
                  line-height: 1.2;
                }
                
                .print-unit-name {
                  text-align: center;
                  font-size: 16px;
                  font-weight: bold;
                  margin-bottom: 8px;
                  color: #000;
                }
                
                .print-title {
                  text-align: center;
                  font-size: 18px;
                  font-weight: bold;
                  margin-bottom: 10px;
                  color: #000;
                }
                
                /* 结算期独立显示在左上角 */
                .print-period-info {
                  text-align: left;
                  margin-bottom: 10px;
                  font-size: 12px;
                  
                  .period-label {
                    font-weight: bold;
                    margin-right: 5px;
                  }
                  
                  .period-value {
                    font-weight: normal;
                  }
                }
                
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 0;
                  font-size: 12px;
                }
                
                .print-info-table,
                .print-materials-table {
                  border: 2px solid #000;
                }
                
                .print-signature-table {
                  border: none;
                  table-layout: fixed;
                  width: 100%;
                }
                
                /* 移除签名表格所有单元格的边框 */
                .print-signature-table td {
                  border: none !important;
                  padding: 6px 4px;
                  font-size: 12px;
                  vertical-align: middle;
                  width: 25%;
                }
                
                td, th {
                  border: 1px solid #000;
                  padding: 6px 4px;
                  font-size: 12px;
                  vertical-align: middle;
                  text-align: center;
                }
                
                
                .info-label {
                  background-color: #f0f0f0;
                  font-weight: bold;
                  text-align: center;
                  width: 80px;
                }
                
                .info-value {
                  text-align: left;
                  padding-left: 8px;
                  font-weight: normal;
                }
                
                .print-materials-table th {
                  background-color: #f0f0f0;
                  font-weight: bold;
                  text-align: center;
                  padding: 8px 4px;
                }
                
                .print-materials-table td {
                  text-align: center;
                  padding: 8px 4px;
                }
                
                .col-serial {
                  width: 3%;
                }
                
                .col-order {
                  width: 6%;
                }
                
                .col-name {
                  width: 8%;
                }
                
                .col-spec {
                  width: 8%;
                }
                
                .col-material {
                  width: 6%;
                }
                
                .col-unit {
                  width: 4%;
                }
                
                .col-quantity {
                  width: 5%;
                }
                
                .col-date {
                  width: 6%;
                }
                
                .col-price {
                  width: 5%;
                }
                
                .col-amount {
                  width: 6%;
                }
                
                .col-tax-rate {
                  width: 4%;
                }
                
                .col-tax-amount {
                  width: 5%;
                }
                
                .col-remark {
                  width: 5%;
                }
                
                .total-row {
                  font-weight: bold;
                  background-color: #f8f8f8;
                }
                
                .empty-row td {
                  height: 25px;
                  border-bottom: 1px solid #000;
                }
                
                .signature-cell {
                  padding: 20px 8px;
                  font-size: 12px;
                  width: 25%;
                  height: 50px;
                  vertical-align: top;
                  text-align: left;
                }
                
                .signature-label {
                  font-weight: normal;
                }
                
                
                .signature-space {
                  border-bottom: 1px solid #000;
                  height: 20px;
                  margin-top: 10px;
                }
                
                @media print {
                  body {
                    padding: 10mm;
                    font-size: 11px;
                  }
                  
                  @page {
                    margin: 15mm;
                    size: A4 landscape;
                  }
                  
                  .print-title {
                    font-size: 16px;
                  }
                  
                  table {
                    font-size: 11px;
                  }
                  
                  td, th {
                    font-size: 11px;
                    padding: 4px 3px;
                  }
                  
                  .signature-cell {
                    font-size: 11px;
                    padding: 15px 6px;
                  }
                  
                  /* 变动价表格打印样式 */
                  .print-materials-table.floating-price th,
                  .print-materials-table.floating-price td {
                    font-size: 10px !important;
                    padding: 3px 2px !important;
                  }
                }
                
                /* 变动价表格列宽 */
                .print-materials-table.floating-price .col-serial {
                  width: 4%;
                }
                
                .print-materials-table.floating-price .col-date {
                  width: 7%;
                }
                
                .print-materials-table.floating-price .col-name {
                  width: 10%;
                }
                
                .print-materials-table.floating-price .col-spec {
                  width: 12%;
                }
                
                .print-materials-table.floating-price .col-quantity {
                  width: 6%;
                }
                
                .print-materials-table.floating-price .col-unit {
                  width: 4%;
                }
                
                .print-materials-table.floating-price .col-base-price {
                  width: 7%;
                }
                
                .print-materials-table.floating-price .col-floating-price {
                  width: 7%;
                }
                
                .print-materials-table.floating-price .col-unit-price {
                  width: 7%;
                }
                
                .print-materials-table.floating-price .col-inclusive-amount {
                  width: 8%;
                }
                
                .print-materials-table.floating-price .col-procurement-fee {
                  width: 7%;
                }
                
                .print-materials-table.floating-price .col-total-amount {
                  width: 8%;
                }
                
                .print-materials-table.floating-price .col-tax-amount {
                  width: 7%;
                }
                
                .print-materials-table.floating-price .col-remark {
                  width: 6%;
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        
        await installPlatformPrintFooter(printWindow, null, {
          documentType: 'SETTLEMENT',
          documentKey: this.settlementId
        });
        printWindow.document.close();
        
        // 关闭加载状态
        loadingInstance.close();
        
        // 等待内容加载完成后打印
        printWindow.onload = function() {
          printWindow.focus();
          printWindow.print();
          // 打印完成后关闭窗口
          printWindow.onafterprint = function() {
            printWindow.close();
          };
        };
        
      } catch (error) {
        console.error('打印失败:', error);
        this.$message.error('打印失败: ' + (error.message || '网络错误'));
        
        // 确保关闭加载状态
        const loadingInstance = this.$loading.service();
        if (loadingInstance) {
          loadingInstance.close();
        }
      }
    },
    
    // 构建签名图片URL
    buildSignatureImageUrl(filePath) {
      if (!filePath) {
        return null;
      }
      
      // 如果是完整的URL（http/https开头），直接使用
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
      }
      
      // 如果是data URL（base64），直接使用
      if (filePath.startsWith('data:')) {
        return filePath;
      }
      
      // 否则，使用附件下载接口
      const apiUrl = getApiUrl('/attachments/download');
      return `${apiUrl}?filePath=${encodeURIComponent(filePath)}`;
    },
    
    // 生成打印内容（固定价）
    generatePrintContent(printData, demandOperatorSignatureUrl = null, supplierOperatorSignatureUrl = null, demandAuditorSignatureUrl = null, supplierAuditorSignatureUrl = null) {
      // 格式化日期
      const formatPrintDate = (dateString) => {
        if (!dateString) return '-';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch (e) {
          return dateString || '-';
        }
      };
      
      // 格式化时间（使用当前时间）
      const formatPrintDateTime = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      };
      
      // 构建签名图片HTML
      const buildSignatureHtml = (signatureUrl) => {
        if (signatureUrl) {
          return `<img src="${signatureUrl}" alt="签名" style="max-width: 150px; max-height: 80px; display: inline-block; vertical-align: middle; margin-left: 10px; object-fit: contain;" />`;
        }
        return '';
      };
      
      // 生成产品列表HTML - 使用详情页面的settlementItems数据
      const hasProcurementFee = this.hasProcurementManagementFee();
      const hasFloatingPrice = this.hasFloatingPriceInSettlement();
      const isFixedPriceTotalMode = this.isFixedPriceProcurementManagementFee() && 
                                    this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT';
      const showProcurementFeeCols = this.showProcurementManagementFeeColumns(); // 仅当清单中存在采管费/采管费金额不为0时显示
      const showActualUnitPriceCol = hasProcurementFee && !hasFloatingPrice && !isFixedPriceTotalMode; // 实际单价列只在固定价产品且采管费不为0时显示，合计模式不显示
      
      const generateProductListHTML = () => {
        const items = this.settlement.settlementItems || [];
        const colCount = showProcurementFeeCols ? 13 : 11;
        if (items.length === 0) {
          return `<tr><td colspan="${colCount}">暂无产品数据</td></tr>`;
        }
        
        let html = '';
        
        items.forEach((item, index) => {
          html += `
            <tr>
              <td class="col-serial">${index + 1}</td>
              <td class="col-order">${item.orderNumber || item.order?.order_id || '-'}</td>
              <td class="col-date">${this.formatDate(item.receivedTime)}</td>
              <td class="col-name">${item.materialsName || item.itemProductName || item.product_name || '-'}</td>
              <td class="col-spec">${this.truncateText(this.getSpecificationText(item), 15) || '-'}</td>
              <td class="col-quantity">${item.settledQuantity || item.settlement_quantity || item.receiptQuantity || '-'}</td>
              <td class="col-unit">${item.unit || item.itemUnit || item.product_unit || '-'}</td>
              <td class="col-price">${this.formatUnitPrice(item.itemTaxInclusiveUnitPrice || item.unitPrice || item.product_price || 0)}</td>
              ${showProcurementFeeCols ? `<td class="col-price">${this.formatProcurementFeeUnitPrice(this.calculateProcurementManagementFeeUnitPrice(item))}</td>` : ''}
              ${showProcurementFeeCols ? `<td class="col-amount">${this.formatAmount(item.procurementManagementFee || 0)}</td>` : ''}
              ${showActualUnitPriceCol ? `<td class="col-price">${this.formatUnitPrice(this.calculateActualUnitPrice(item))}</td>` : ''}
              <td class="col-amount">${this.formatAmount(this.calculateSettlementItemInclusiveAmount(item))}</td>
              <td class="col-tax-rate">${((item.taxRate || item.itemTaxRate || item.product_tax_rate || 0) * 100).toFixed(1)}%</td>
              <td class="col-tax-amount">${this.formatAmount(this.calculateItemTaxAmount(item))}</td>
              <td class="col-amount">${this.formatAmount(this.calculateTaxExclusiveAmount(item))}</td>
            </tr>
          `;
        });
        
        // 计算小计金额
        const subtotalAmount = this.calculateSettlementItemsTotal();
        const subtotalTax = this.calculateSettlementItemsTaxTotal();
        const subtotalTaxExclusive = this.calculateSettlementItemsTaxExclusiveTotal();
        
        // 计算采管费总额
        const procurementFeeTotal = isFixedPriceTotalMode ? this.calculateFixedPriceTotalProcurementManagementFee() : 0;
        
        if (isFixedPriceTotalMode) {
          // 合计模式：显示小计行、采管费金额行、合计行
          // 表格列数：序号(1) + 订单编号(2) + 收货日期(3) + 材料名称(4) + 规格型号(5) + 收货数量(6) + 单位(7) + 含税单价(8) + 实际单价(9，如果有) + 含税金额(10) + 税率(11) + 税额(12) + 不含税金额(13)
          // 前7列（序号到单位） + 含税单价(1列) + 实际单价(1列，如果有) = 需要colspan的值

          // 计算平均税率：通过小计的税额和不含税金额计算
          // 平均税率 = 小计税额 / 小计不含税金额
          let averageTaxRate = 0;
          if (subtotalTaxExclusive !== 0) {
            averageTaxRate = subtotalTax / subtotalTaxExclusive;
          }

          // 计算采管费税额：采管费 × 税率 / (1 + 税率)
          // 支持负数采管费，税额也会是负数
          const procurementFeeTaxAmount = averageTaxRate !== 0 && procurementFeeTotal !== 0
            ? (procurementFeeTotal * averageTaxRate / (1 + averageTaxRate))
            : 0;

          // 计算采管费不含税金额：采管费 - 采管费税额
          const procurementFeeTaxExclusiveAmount = procurementFeeTotal - procurementFeeTaxAmount;

          // 计算含税金额合计（含税金额小计 + 采管费总额）
          const totalTaxInclusiveAmount = subtotalAmount + procurementFeeTotal;

          // 计算税额合计（小计税额 + 采管费税额）
          const totalTaxAmount = subtotalTax + procurementFeeTaxAmount;

          // 计算不含税金额合计（小计不含税金额 + 采管费不含税金额）
          const totalTaxExclusiveAmount = subtotalTaxExclusive + procurementFeeTaxExclusiveAmount;

          // 固定价合计模式下的列结构：序号(1) + 订单编号到单位(6) + 含税单价(1) + 含税金额(1) + 税率(1) + 税额(1) + 不含税金额(1) = 12列
          // colspan应该覆盖：订单编号到含税单价 = 6 + 1 = 7列
          const colspanBeforeAmount = 7; // 固定价合计模式：订单编号到含税单价共7列

          html += `
            <tr class="total-row">
              <td class="col-serial">${items.length + 1}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">小计</td>
              <td class="col-amount">${this.formatAmount(subtotalAmount)}</td>
              <td class="col-tax-rate"></td>
              <td class="col-tax-amount">${this.formatAmount(subtotalTax)}</td>
              <td class="col-amount">${this.formatAmount(subtotalTaxExclusive)}</td>
            </tr>
            <tr class="total-row">
              <td class="col-serial">${items.length + 2}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">采管费金额</td>
              <td class="col-amount">${this.formatAmount(procurementFeeTotal)}</td>
              <td class="col-tax-rate">${averageTaxRate !== 0 ? ((averageTaxRate * 100).toFixed(1) + '%') : ''}</td>
              <td class="col-tax-amount">${this.formatAmount(procurementFeeTaxAmount)}</td>
              <td class="col-amount">${this.formatAmount(procurementFeeTaxExclusiveAmount)}</td>
            </tr>
            <tr class="total-row">
              <td class="col-serial">${items.length + 3}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">合计</td>
              <td class="col-amount">${this.formatAmount(totalTaxInclusiveAmount)}</td>
              <td class="col-tax-rate"></td>
              <td class="col-tax-amount">${this.formatAmount(totalTaxAmount)}</td>
              <td class="col-amount">${this.formatAmount(totalTaxExclusiveAmount)}</td>
            </tr>
          `;
        } else {
          // 单价模式或其他情况：显示合计行
          html += `
            <tr class="total-row">
              <td class="col-serial">${items.length + 1}</td>
              <td class="col-order" colspan="6">合计</td>
              <td class="col-price"></td>
              ${showProcurementFeeCols ? '<td class="col-price" colspan="2"></td>' : ''}
              ${showActualUnitPriceCol ? '<td class="col-price"></td>' : ''}
              <td class="col-amount">${this.formatAmount(subtotalAmount)}</td>
              <td class="col-tax-rate"></td>
              <td class="col-tax-amount">${this.formatAmount(subtotalTax)}</td>
              <td class="col-amount">${this.formatAmount(subtotalTaxExclusive)}</td>
            </tr>
          `;
        }
        
        return html;
      };
      
      // 判断单位类型并获取单位名称和标题
      let unitName = '';
      let title = '对账结算单';
      
      if (this.isDemandUnit) {
        // 需求单位：显示采购结算单
        title = '采购结算单';
        // 获取需求单位名称（只显示第一个）
        if (this.settlement.demandUnits && this.settlement.demandUnits.length > 0) {
          const unitNames = this.settlement.demandUnits.map(unit => unit.name || unit.unitName).filter(name => name);
          unitName = unitNames.length > 0 ? unitNames[0] : '';
        } else if (printData.demandUnitNames && printData.demandUnitNames.length > 0) {
          unitName = printData.demandUnitNames[0];
        } else {
          unitName = this.getDemandUnitsText();
        }
      } else if (this.isSupplier) {
        // 供应商单位：显示销售结算单
        title = '销售结算单';
        // 获取供应商单位名称
        unitName = this.settlement.supplier?.unitName || this.settlement.supplier_name || printData.supplierName || '';
      }
      
      return `
        ${unitName ? `<div class="print-unit-name">${unitName}</div>` : ''}
        <!-- 标题 -->
        <div class="print-title">${title}</div>
        
        <!-- 结算期 - 独立显示在左上角 -->
        <div class="print-period-info">
          <span class="period-label">结算期：</span>
          <span class="period-value">${formatPrintDate(printData.settlementDate)}</span>
        </div>
        
        <!-- 单位信息 -->
        <table class="print-info-table">
          <tr>
            <td class="info-label">需求单位</td>
            <td class="info-value">${(printData.demandUnitNames && printData.demandUnitNames.length > 0) ? printData.demandUnitNames[0] : '-'}</td>
            <td class="info-label">供货单位</td>
            <td class="info-value">${printData.supplierName || '-'}</td>
          </tr>
          <tr>
            <td class="info-label">二级机构</td>
            <td class="info-value">${(printData.secondaryOrgNames || []).join('、') || '-'}</td>
            <td class="info-label">结算单编号</td>
            <td class="info-value">${printData.settlementNumber || '-'}</td>
          </tr>
        </table>
        
        <!-- 材料明细表格 -->
        <table class="print-materials-table">
          <thead>
            <tr>
              <th class="col-serial">序号</th>
              <th class="col-order">订单编号</th>
              <th class="col-date">收货日期</th>
              <th class="col-name">材料名称</th>
              <th class="col-spec">规格型号</th>
              <th class="col-quantity">收货数量</th>
              <th class="col-unit">单位</th>
              <th class="col-price">含税单价</th>
              ${showProcurementFeeCols ? '<th class="col-price">采管费单价</th>' : ''}
              ${showProcurementFeeCols ? '<th class="col-amount">采管费金额</th>' : ''}
              ${showActualUnitPriceCol ? '<th class="col-price">实际单价</th>' : ''}
              <th class="col-amount">含税金额</th>
              <th class="col-tax-rate">税率</th>
              <th class="col-tax-amount">税额</th>
              <th class="col-amount">不含税金额</th>
            </tr>
          </thead>
          <tbody>
            ${generateProductListHTML()}
          </tbody>
        </table>
        
        <!-- 签名区域（仅当状态为已确认/EXECUTING时显示需求方签名） -->
        <table class="print-signature-table">
          <tr>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">需求单位经办人：</span>
                ${this.settlement.status === 'EXECUTING' ? buildSignatureHtml(demandOperatorSignatureUrl) : ''}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${this.settlement.status === 'EXECUTING' ? (formatPrintDateTime()) : ''}</span>
            </td>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">供应单位经办人：</span>
                ${buildSignatureHtml(supplierOperatorSignatureUrl)}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${formatPrintDateTime()}</span>
            </td>
          </tr>
          <tr>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">需求单位审核人：</span>
                ${printData.demandAuditTime ? buildSignatureHtml(demandAuditorSignatureUrl) : ''}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${printData.demandAuditTime ? formatPrintDate(printData.demandAuditTime) : ''}</span>
            </td>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">供应单位审核人：</span>
                ${printData.supplierAuditTime ? buildSignatureHtml(supplierAuditorSignatureUrl) : ''}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${printData.supplierAuditTime ? formatPrintDate(printData.supplierAuditTime) : ''}</span>
            </td>
          </tr>
        </table>
      `;
    },
    
    // 生成变动价打印内容
    generateFloatingPricePrintContent(printData, demandOperatorSignatureUrl = null, supplierOperatorSignatureUrl = null, demandAuditorSignatureUrl = null, supplierAuditorSignatureUrl = null) {
      // 格式化日期
      const formatPrintDate = (dateString) => {
        if (!dateString) return '-';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch (e) {
          return dateString || '-';
        }
      };
      
      // 格式化时间（使用当前时间）
      const formatPrintDateTime = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      };
      
      // 构建签名图片HTML
      const buildSignatureHtml = (signatureUrl) => {
        if (signatureUrl) {
          return `<img src="${signatureUrl}" alt="签名" style="max-width: 150px; max-height: 80px; display: inline-block; vertical-align: middle; margin-left: 10px; object-fit: contain;" />`;
        }
        return '';
      };
      
      // 生成变动价产品列表HTML - 使用详情页面的settlementItems数据
      const generateFloatingProductListHTML = () => {
        const items = this.settlement.settlementItems || [];
        // 判断是否为变动价合计模式
        const isFloatingPriceTotalMode = this.isFloatingPriceProcurementManagementFee() &&
                                        this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT';
        const showProcurementFeeCols = this.hasProcurementManagementFee() && !isFloatingPriceTotalMode;
        const colCount = showProcurementFeeCols ? 16 : 14; // 合计模式时隐藏采管费列（16-2=14）
        
        if (items.length === 0) {
          return `<tr><td colspan="${colCount}">暂无产品数据</td></tr>`;
        }
        
        let html = '';
        
        items.forEach((item, index) => {
          
          html += `
            <tr>
              <td class="col-serial">${index + 1}</td>
              <td class="col-order">${item.orderNumber || item.order?.order_id || '-'}</td>
              <td class="col-date">${this.formatDate(item.receivedTime)}</td>
              <td class="col-name">${item.materialsName || item.itemProductName || item.product_name || '-'}</td>
              <td class="col-spec">${this.truncateText(this.getSpecificationText(item), 15) || '-'}</td>
              <td class="col-quantity">${item.settledQuantity || item.settlement_quantity || item.receiptQuantity || '-'}</td>
              <td class="col-unit">${item.unit || item.itemUnit || item.product_unit || '-'}</td>
              <td class="col-base-price">${this.formatUnitPrice(item.itemTaxInclusiveBasePrice || 0)}</td>
              <td class="col-floating-price">${this.formatUnitPrice(item.itemTaxInclusiveFloatingPrice || 0)}</td>
              <td class="col-price">${this.formatUnitPrice(this.calculateBaseUnitPrice(item))}</td>
              ${showProcurementFeeCols ? `<td class="col-price">${this.formatProcurementFeeUnitPrice(this.calculateProcurementManagementFeeUnitPrice(item))}</td>` : ''}
              ${showProcurementFeeCols ? `<td class="col-amount">${this.formatAmount(item.procurementManagementFee || 0)}</td>` : ''}
              <td class="col-price">${this.formatUnitPrice(this.calculateSettlementUnitPrice(item))}</td>
              <td class="col-amount">${this.formatAmount(this.calculateSettlementItemInclusiveAmount(item))}</td>
              <td class="col-tax-rate">${((item.taxRate || item.itemTaxRate || item.product_tax_rate || 0) * 100).toFixed(1)}%</td>
              <td class="col-tax-amount">${this.formatAmount(this.calculateItemTaxAmount(item))}</td>
              <td class="col-amount">${this.formatAmount(this.calculateTaxExclusiveAmount(item))}</td>
            </tr>
          `;
        });
        
        // 计算小计金额
        const subtotalAmount = this.calculateSettlementItemsTotal();
        const subtotalTax = this.calculateSettlementItemsTaxTotal();
        const subtotalTaxExclusive = this.calculateSettlementItemsTaxExclusiveTotal();
        
        // 变动价表格列结构：
        // 单价模式：序号(1) + 订单编号到单位(6) + 含税固定价到基础单价(3) + 采管费单价和金额(2) + 结算单价(1) + 含税结算金额(1) + 税率(1) + 税额(1) + 不含税金额(1) = 16列
        // 合计模式：序号(1) + 订单编号到单位(6) + 含税固定价到基础单价(3) + 结算单价(1) + 含税结算金额(1) + 税率(1) + 税额(1) + 不含税金额(1) = 14列
        // colspan应该覆盖：订单编号到结算单价
        // 单价模式：6 + 3 + 2 + 1 = 12列
        // 合计模式：6 + 3 + 1 = 10列
        const colspanBeforeAmount = showProcurementFeeCols ? 12 : 10;
        
        if (isFloatingPriceTotalMode) {
          // 合计模式：显示小计行、采管费金额行、合计行
          // 计算采管费总额
          const procurementFeeTotal = this.calculateFloatingPriceTotalProcurementManagementFee();
          
          // 计算平均税率：通过小计的税额和不含税金额计算
          // 平均税率 = 小计税额 / 小计不含税金额
          let averageTaxRate = 0;
          if (subtotalTaxExclusive !== 0) {
            averageTaxRate = subtotalTax / subtotalTaxExclusive;
          }

          // 计算采管费税额：采管费 × 税率 / (1 + 税率)
          // 支持负数采管费，税额也会是负数
          const procurementFeeTaxAmount = averageTaxRate !== 0 && procurementFeeTotal !== 0
            ? (procurementFeeTotal * averageTaxRate / (1 + averageTaxRate))
            : 0;

          // 计算采管费不含税金额：采管费 - 采管费税额
          const procurementFeeTaxExclusiveAmount = procurementFeeTotal - procurementFeeTaxAmount;

          // 计算含税金额合计（含税金额小计 + 采管费总额）
          const totalTaxInclusiveAmount = subtotalAmount + procurementFeeTotal;

          // 计算税额合计（小计税额 + 采管费税额）
          const totalTaxAmount = subtotalTax + procurementFeeTaxAmount;

          // 计算不含税金额合计（小计不含税金额 + 采管费不含税金额）
          const totalTaxExclusiveAmount = subtotalTaxExclusive + procurementFeeTaxExclusiveAmount;

          html += `
            <tr class="total-row">
              <td class="col-serial">${items.length + 1}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">小计</td>
              <td class="col-amount">${this.formatAmount(subtotalAmount)}</td>
              <td class="col-tax-rate"></td>
              <td class="col-tax-amount">${this.formatAmount(subtotalTax)}</td>
              <td class="col-amount">${this.formatAmount(subtotalTaxExclusive)}</td>
            </tr>
            <tr class="total-row">
              <td class="col-serial">${items.length + 2}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">采管费金额</td>
              <td class="col-amount">${this.formatAmount(procurementFeeTotal)}</td>
              <td class="col-tax-rate">${averageTaxRate !== 0 ? ((averageTaxRate * 100).toFixed(1) + '%') : ''}</td>
              <td class="col-tax-amount">${this.formatAmount(procurementFeeTaxAmount)}</td>
              <td class="col-amount">${this.formatAmount(procurementFeeTaxExclusiveAmount)}</td>
            </tr>
            <tr class="total-row">
              <td class="col-serial">${items.length + 3}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">合计</td>
              <td class="col-amount">${this.formatAmount(totalTaxInclusiveAmount)}</td>
              <td class="col-tax-rate"></td>
              <td class="col-tax-amount">${this.formatAmount(totalTaxAmount)}</td>
              <td class="col-amount">${this.formatAmount(totalTaxExclusiveAmount)}</td>
            </tr>
          `;
        } else {
          // 单价模式或其他情况：显示合计行
          html += `
            <tr class="total-row">
              <td class="col-serial">${items.length + 1}</td>
              <td class="col-order" colspan="${colspanBeforeAmount}">合计</td>
              <td class="col-amount">${this.formatAmount(subtotalAmount)}</td>
              <td class="col-tax-rate"></td>
              <td class="col-tax-amount">${this.formatAmount(subtotalTax)}</td>
              <td class="col-amount">${this.formatAmount(subtotalTaxExclusive)}</td>
            </tr>
          `;
        }
        
        return html;
      };
      
      // 判断单位类型并获取单位名称和标题
      let unitName = '';
      let title = '对账结算单（变动价）';
      
      if (this.isDemandUnit) {
        // 需求单位：显示采购结算单
        title = '采购结算单（变动价）';
        // 获取需求单位名称（只显示第一个）
        if (this.settlement.demandUnits && this.settlement.demandUnits.length > 0) {
          const unitNames = this.settlement.demandUnits.map(unit => unit.name || unit.unitName).filter(name => name);
          unitName = unitNames.length > 0 ? unitNames[0] : '';
        } else if (printData.demandUnitNames && printData.demandUnitNames.length > 0) {
          unitName = printData.demandUnitNames[0];
        } else {
          unitName = this.getDemandUnitsText();
        }
      } else if (this.isSupplier) {
        // 供应商单位：显示销售结算单
        title = '销售结算单（变动价）';
        // 获取供应商单位名称
        unitName = this.settlement.supplier?.unitName || this.settlement.supplier_name || printData.supplierName || '';
      }
      
      return `
        ${unitName ? `<div class="print-unit-name">${unitName}</div>` : ''}
        <!-- 标题 -->
        <div class="print-title">${title}</div>
        
        <!-- 结算期 - 独立显示在左上角 -->
        <div class="print-period-info">
          <span class="period-label">结算期：</span>
          <span class="period-value">${formatPrintDate(printData.settlementDate)}</span>
        </div>
        
        <!-- 单位信息 -->
        <table class="print-info-table">
          <tr>
            <td class="info-label">需求单位</td>
            <td class="info-value">${(printData.demandUnitNames && printData.demandUnitNames.length > 0) ? printData.demandUnitNames[0] : '-'}</td>
            <td class="info-label">供货单位</td>
            <td class="info-value">${printData.supplierName || '-'}</td>
          </tr>
          <tr>
            <td class="info-label">二级机构</td>
            <td class="info-value">${(printData.secondaryOrgNames || []).join('、') || '-'}</td>
            <td class="info-label">结算单编号</td>
            <td class="info-value">${printData.settlementNumber || '-'}</td>
          </tr>
          <tr>
            <td class="info-label">采管费类型</td>
            <td class="info-value">${printData.procurementManagementFeeType === 'FIXED_RATE' ? '固定比率' : (printData.procurementManagementFeeType === 'FIXED_UNIT_PRICE' ? '固定单价' : '-')}</td>
            <td class="info-label">${printData.procurementManagementFeeType === 'FIXED_RATE' ? '采管费（%）' : (printData.procurementManagementFeeType === 'FIXED_UNIT_PRICE' ? '采管费单价' : '')}</td>
            <td class="info-value">${printData.procurementManagementFeeType === 'FIXED_RATE' ? ((printData.procurementManagementFeeRate || 0) * 100).toFixed(4) + '%' : (printData.procurementManagementFeeType === 'FIXED_UNIT_PRICE' ? (printData.procurementManagementFeeMode === 'TOTAL_AMOUNT' ? this.formatAmount(printData.procurementManagementFeeUnitPrice || 0) : this.formatUnitPrice(printData.procurementManagementFeeUnitPrice || 0)) + ' 元' : '-')}</td>
          </tr>
        </table>
        
        <!-- 材料明细表格 - 变动价 -->
        <table class="print-materials-table floating-price">
          <thead>
            <tr>
              <th class="col-serial">序号</th>
              <th class="col-order">订单编号</th>
              <th class="col-date">收货日期</th>
              <th class="col-name">材料名称</th>
              <th class="col-spec">规格型号</th>
              <th class="col-quantity">收货数量</th>
              <th class="col-unit">单位</th>
              <th class="col-base-price">含税固定价</th>
              <th class="col-floating-price">变动价</th>
              <th class="col-price">基础单价</th>
              ${this.showProcurementManagementFeeColumns() ? '<th class="col-price">采管费单价</th>' : ''}
              ${this.showProcurementManagementFeeColumns() ? '<th class="col-amount">采管费金额</th>' : ''}
              <th class="col-price">结算单价</th>
              <th class="col-amount">含税结算金额</th>
              <th class="col-tax-rate">税率</th>
              <th class="col-tax-amount">税额</th>
              <th class="col-amount">不含税金额</th>
            </tr>
          </thead>
          <tbody>
            ${generateFloatingProductListHTML()}
          </tbody>
        </table>
        
        <!-- 签名区域（仅当状态为已确认/EXECUTING时显示需求方签名） -->
        <table class="print-signature-table">
          <tr>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">需求单位经办人：</span>
                ${this.settlement.status === 'EXECUTING' ? buildSignatureHtml(demandOperatorSignatureUrl) : ''}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${this.settlement.status === 'EXECUTING' ? (formatPrintDateTime()) : ''}</span>
            </td>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">供应单位经办人：</span>
                ${buildSignatureHtml(supplierOperatorSignatureUrl)}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${formatPrintDateTime()}</span>
            </td>
          </tr>
          <tr>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">需求单位审核人：</span>
                ${printData.demandAuditTime ? buildSignatureHtml(demandAuditorSignatureUrl) : ''}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${printData.demandAuditTime ? formatPrintDate(printData.demandAuditTime) : ''}</span>
            </td>
            <td class="signature-cell">
              <div style="display: flex; align-items: center;">
                <span class="signature-label">供应单位审核人：</span>
                ${printData.supplierAuditTime ? buildSignatureHtml(supplierAuditorSignatureUrl) : ''}
              </div>
            </td>
            <td class="signature-cell">
              <span class="signature-label">操作时间：</span>
              <span class="signature-value">${printData.supplierAuditTime ? formatPrintDate(printData.supplierAuditTime) : ''}</span>
            </td>
          </tr>
        </table>
      `;
    },
    
    // 获取规格型号文本
    getSpecificationText(item) {
      if (!item) return '';
      return (
        item.materialsSpec ||
        item.itemSpecification ||
        item.materials_spec ||
        item.product_spec ||
        item.specification ||
        ''
      );
    },

    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 格式化金额
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00';
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 格式化单价（固定保留5位小数）
    formatUnitPrice(amount) {
      if (amount === undefined || amount === null) return '0.00000';
      const num = Number(amount);
      return isNaN(num) ? '0.00000' : num.toFixed(5);
    },

    roundUnitPrice(amount) {
      const num = Number(amount);
      return isNaN(num) ? 0 : Number(num.toFixed(5));
    },
    
    // 格式化采管费单价
    formatProcurementFeeUnitPrice(amount) {
      return this.formatUnitPrice(amount);
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        // 处理ISO格式日期
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          // 如果不是有效日期，尝试其他格式解析
          if (typeof dateString === 'string') {
            // 尝试解析常见的日期格式 YYYY-MM-DD
            const parts = dateString.split('-');
            if (parts.length === 3) {
              return dateString; // 已经是YYYY-MM-DD格式
            }
          }
          return dateString || '-';
        }
        
        // 格式化为YYYY-MM-DD
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } catch (e) {
        console.error('日期格式化错误:', e);
        return dateString || '-';
      }
    },
    
    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return '-';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return dateString || '-';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        console.error('日期时间格式化错误:', e);
        return dateString || '-';
      }
    },
    
    // 根据状态获取Tag类型
    getStatusTagType(status) {
      if (!status) return 'info';
      
      switch (status) {
        case 'DRAFT':
          return 'warning';
        case 'ACTIVE':
          return 'info';
        case 'EXECUTING':
          return 'warning';
        case 'INVOICED':
          return 'primary';
        case 'FINISHED':
          return 'success';
        case 'CANCELLED':
          return 'danger';
        case '待确认':
          return 'info';
        case '已确认':
          return 'warning';
        case '已开票':
          return 'primary';
        case '结算完成':
          return 'success';
        case '已取消':
          return 'danger';
        default:
          return 'info';
      }
    },
    
    // 获取状态显示文本
    getStatusText(status) {
      if (!status) return '-';
      
      switch (status) {
        case 'DRAFT':
          return '供应商待审核';
        case 'ACTIVE':
          return '待确认';
        case 'EXECUTING':
          return '已确认';
        case 'INVOICED':
          return '已开票';
        case 'FINISHED':
          return '结算完成';
        case 'CANCELLED':
          return '已取消';
        default:
          return status;
      }
    },
    
    // 获取订单状态类型
    getOrderStatusType(status) {
      if (!status) return 'info';
      
      switch (status) {
        case '待结算':
          return 'info';
        case '未开票':
          return 'warning';
        case '已完成':
          return 'success';
        case '已开票':
          return 'success';
        case '已取消':
          return 'danger';
        default:
          return 'info';
      }
    },
    
    // 返回列表页
    goBack() {
      this.$router.push('/settlement/settlements-list');
    },
    
    // 计算订单明细汇总
    calculateOrderItemsSummary(items) {
      this.orderItemsTotalAmount = 0;
      this.orderItemsTotalQuantity = 0;
      this.orderItemsTotalTax = 0;
      
      if (!items || !Array.isArray(items) || items.length === 0) {
        return;
      }
      
      items.forEach(item => {
        // 确保数据为数字
        const priceTotal = Number(item.price_total || 0);
        const quantity = Number(item.settlement_quantity || 0); // 使用结算数量
        
        // 计算税额差值（含税金额 - 不含税金额）
        const taxAmount = Number(item.product_tax_price || 0) - priceTotal;
        
        this.orderItemsTotalAmount += priceTotal;
        this.orderItemsTotalQuantity += quantity;
        this.orderItemsTotalTax += taxAmount > 0 ? taxAmount : 0;
      });
      
    },
    
    // 结算确认
    confirmSettlement() {
      // 重置确认表单
      this.confirmForm = {
        needAudit: false,
        auditorId: null,
        remark: ''
      };
      
      // 清空审核人列表
      this.auditorList = [];
      
      // 显示确认对话框
      this.confirmDialogVisible = true;
    },
    
    // 提交确认表单
    submitConfirmForm() {
      this.$refs.confirmForm.validate(valid => {
        if (!valid) {
          return false;
        }
        
        this.confirmLoading = true;
        
        const confirmData = {
          needAudit: this.confirmForm.needAudit,
          auditorId: this.confirmForm.needAudit ? this.confirmForm.auditorId : null
        };
        
        // 调用确认结算接口
        confirm_settlement(this.settlementId, confirmData)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('结算确认成功');
              this.confirmDialogVisible = false;
              // 重新加载结算单详情
              this.getSettlementDetail();
            } else {
              this.$message.error(response.data?.message || '结算确认失败');
            }
            this.confirmLoading = false;
          })
          .catch(error => {
            console.error('结算确认失败:', error);
            this.$message.error('结算确认失败，请稍后重试');
            this.confirmLoading = false;
          });
      });
    },
    
    // 审核结算
    auditSettlement() {
      this.$confirm('确定要审核通过此结算单吗？', '审核确认', {
        confirmButtonText: '确定通过',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.auditLoading = true;
        
        const auditData = {
          approved: true,
          auditRemark: '审核通过'
        };
        
        // 调用审核结算接口
        audit_settlement(this.settlementId, auditData)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('审核通过成功');
              // 重新加载结算单详情
              this.getSettlementDetail();
            } else {
              this.$message.error(response.data?.message || '审核失败');
            }
            this.auditLoading = false;
          })
          .catch(error => {
            console.error('审核提交失败:', error);
            this.$message.error('审核失败，请稍后重试');
            this.auditLoading = false;
          });
      }).catch(() => {
        // 用户取消操作
        this.$message.info('已取消审核操作');
      });
    },
    
    // 回退结算状态
    rollbackSettlement() {
      this.$confirm('确定要将结算单状态回退到待确认吗？回退后可以重新进行确认或修改。', '回退确认', {
        confirmButtonText: '确定回退',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.rollbackLoading = true;
        
        // 调用回退结算接口
        rollback_settlement(this.settlementId)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('结算单状态回退成功');
              // 重新加载结算单详情
              this.getSettlementDetail();
            } else {
              this.$message.error(response.data?.message || '回退失败');
            }
            this.rollbackLoading = false;
          })
          .catch(error => {
            console.error('回退结算状态失败:', error);
            this.$message.error('回退失败，请稍后重试');
            this.rollbackLoading = false;
          });
      }).catch(() => {
        // 用户取消操作
        this.$message.info('已取消回退操作');
      });
    },
    
    // 完成结算
    finishSettlement() {
      this.$confirm('确定要完成结算吗？完成后将无法再进行修改。', '完成结算确认', {
        confirmButtonText: '确定完成',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.finishLoading = true;
        
        // 调用完成结算API接口
        finish_settlement(this.settlementId)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('结算完成成功');
              // 重新加载结算单详情
              this.getSettlementDetail();
            } else {
              this.$message.error(response.data?.message || '结算完成失败');
            }
            this.finishLoading = false;
          })
          .catch(error => {
            console.error('完成结算失败:', error);
            this.$message.error('完成结算失败，请稍后重试');
            this.finishLoading = false;
          });
      }).catch(() => {
        // 用户取消操作
        this.$message.info('已取消完成结算操作');
      });
    },
    
    // 供应商审核结算
    supplierAuditSettlement() {
      this.$confirm('确定要审核通过此结算单吗？审核通过后需求方将可以看到此结算单。', '供应商审核确认', {
        confirmButtonText: '确定通过',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.supplierAuditLoading = true;
        
        const auditData = {
          approved: true,
          auditRemark: '供应商审核通过'
        };
        
        // 调用供应商审核结算接口
        supplier_audit_settlement(this.settlementId, auditData)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('供应商审核通过成功，结算单现在对需求方可见');
              // 重新加载结算单详情
              this.getSettlementDetail();
            } else {
              this.$message.error(response.data?.message || '供应商审核失败');
            }
            this.supplierAuditLoading = false;
          })
          .catch(error => {
            console.error('供应商审核提交失败:', error);
            this.$message.error('供应商审核失败，请稍后重试');
            this.supplierAuditLoading = false;
          });
      }).catch(() => {
        // 用户取消操作
        this.$message.info('已取消供应商审核操作');
      });
    },
    
    // 格式化税率
    formatTaxRate(rate) {
      if (rate === undefined || rate === null) return '0';
      
      // 如果税率已经是百分比格式（如"13.00"），直接返回
      const numRate = Number(rate);
      return isNaN(numRate) ? '0' : numRate.toFixed(2);
    },
    
    // 计算结算项目总金额
    calculateSettlementItemsTotal() {
      if (!this.settlement.settlementItems) return 0;
      
      return this.settlement.settlementItems.reduce((total, item) => {
        const inclusiveAmount = this.getSettlementItemInclusiveAmount(item);
        return total + inclusiveAmount;
      }, 0);
    },
    
    // 计算结算项目税额合计
    calculateSettlementItemsTaxTotal() {
      if (!this.settlement.settlementItems) return 0;
      
      return this.settlement.settlementItems.reduce((total, item) => {
        const taxAmount = this.calculateItemTaxAmount(item);
        return total + taxAmount;
      }, 0);
    },
    
    // 计算结算项目不含税金额合计
    calculateSettlementItemsTaxExclusiveTotal() {
      if (!this.settlement.settlementItems) return 0;
      
      return this.settlement.settlementItems.reduce((total, item) => {
        const taxExclusiveAmount = this.calculateTaxExclusiveAmount(item);
        return total + taxExclusiveAmount;
      }, 0);
    },
    
    // 计算结算项目采管费金额合计
    calculateSettlementItemsProcurementFeeTotal() {
      if (!this.settlement.settlementItems) return 0;
      
      return this.settlement.settlementItems.reduce((total, item) => {
        const procurementFee = Number(item.procurementManagementFee || 0);
        return total + procurementFee;
      }, 0);
    },

    // 计算结算单采管费总额：合计模式取结算单配置，单价模式取明细合计
    calculateSettlementProcurementManagementFeeTotal() {
      if (this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        if (this.isFloatingPriceProcurementManagementFee()) {
          return this.calculateFloatingPriceTotalProcurementManagementFee();
        }
        if (this.isFixedPriceProcurementManagementFee()) {
          return this.calculateFixedPriceTotalProcurementManagementFee();
        }
      }
      return this.calculateSettlementItemsProcurementFeeTotal();
    },
    
    // 表格合计行计算方法
    getTableSummary({ columns }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '合计';
          return;
        }
        const label = column.label;
        // 根据列的label来判断需要显示哪个合计值
        if (label === '采管费金额' && this.showProcurementManagementFeeColumns()) {
          sums[index] = this.formatAmount(this.calculateSettlementItemsProcurementFeeTotal());
        } else if (label === '含税金额') {
          sums[index] = this.formatAmount(this.calculateSettlementItemsTotal());
        } else if (label === '税额') {
          sums[index] = this.formatAmount(this.calculateSettlementItemsTaxTotal());
        } else if (label === '不含税金额') {
          sums[index] = this.formatAmount(this.calculateSettlementItemsTaxExclusiveTotal());
        } else {
          sums[index] = '';
        }
      });
      return sums;
    },
    
    // 格式化结算期
    formatSettlementPeriod() {
      const startDate = this.settlement.period_start || this.settlement.periodStart;
      const endDate = this.settlement.period_end || this.settlement.periodEnd;
      
      if (!startDate || !endDate) {
        return '-';
      }
      
      return `${this.formatDate(startDate)}-${this.formatDate(endDate)}`;
    },
    
    // 获取打印材料列表
    getPrintMaterialsList() {
      // 优先使用settlementItems
      if (this.settlement.settlementItems && this.settlement.settlementItems.length > 0) {
        return this.settlement.settlementItems;
      }
      
      // 如果没有settlementItems，尝试从orderItems获取
      if (this.orderItems && this.orderItems.length > 0) {
        return this.orderItems.map(item => ({
          materialsName: item.product_name,
          materialsSpec: item.product_spec || item.materials_spec,
          unit: item.product_unit,
          settledQuantity: item.settlement_quantity,
          unitPrice: item.product_price,
          settledAmount: item.price_total,
          remark: item.remark
        }));
      }
      
      return [];
    },
    
    // 检查结算单中是否有变动价产品
    hasFloatingPriceInSettlement() {
      if (!this.settlement.settlementItems || this.settlement.settlementItems.length === 0) {
        return false;
      }
      
      return this.settlement.settlementItems.some(item => 
        item.itemPricingMethod === 'FLOATING'
      );
    },
    
    // 检查结算单中是否有固定价产品
    hasFixedPriceInSettlement() {
      if (!this.settlement.settlementItems || this.settlement.settlementItems.length === 0) {
        return false;
      }
      
      return this.settlement.settlementItems.some(item => 
        item.itemPricingMethod === 'FIXED' || !item.itemPricingMethod
      );
    },
    
    // 检查结算单是否配置了采管费
    hasProcurementManagementFee() {
      return this.settlement.procurementManagementFeeType && 
        (this.settlement.procurementManagementFeeType === 'FIXED_RATE' || 
         this.settlement.procurementManagementFeeType === 'FIXED_UNIT_PRICE');
    },
    
    // 检查是否为固定价采管费
    isFixedPriceProcurementManagementFee() {
      return this.hasFixedPriceInSettlement() && this.hasProcurementManagementFee();
    },
    
    // 检查是否为变动价采管费
    isFloatingPriceProcurementManagementFee() {
      return this.hasFloatingPriceInSettlement() && this.hasProcurementManagementFee();
    },
    
    // 是否显示采管费列（固定价或变动价合计模式时隐藏；且仅当清单中存在采管费/采管费金额不为0时显示）
    showProcurementManagementFeeColumns() {
      // 如果是固定价产品且是合计模式，隐藏采管费列
      if (this.isFixedPriceProcurementManagementFee() && 
          this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        return false;
      }
      // 如果是变动价产品且是合计模式，隐藏采管费列
      if (this.isFloatingPriceProcurementManagementFee() && 
          this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        return false;
      }
      // 无采管费配置时不显示
      if (!this.hasProcurementManagementFee()) {
        return false;
      }
      // 仅当清单中至少有一项采管费或采管费金额不为0时显示这两列
      const items = this.settlement.settlementItems || [];
      const hasNonZeroFee = items.some(item => {
        const fee = Number(item.procurementManagementFee || 0);
        const unitPrice = this.calculateProcurementManagementFeeUnitPrice(item);
        return fee !== 0 || unitPrice !== 0;
      });
      return hasNonZeroFee;
    },
    
    // 计算基础单价（基础价 + 变动价，仅用于变动价产品）
    calculateBaseUnitPrice(item) {
      if (item.itemPricingMethod === 'FLOATING') {
        // 变动价产品：基础价 + 变动价
        const basePrice = item.itemTaxInclusiveBasePrice || 0;
        const floatingPrice = item.itemTaxInclusiveFloatingPrice || 0;
        return this.roundUnitPrice(basePrice + floatingPrice);
      }
      return 0;
    },
    
    // 计算结算单价（基础单价 + 采管费单价，仅用于变动价产品）
    calculateSettlementUnitPrice(item) {
      if (item.itemPricingMethod === 'FLOATING') {
        // 变动价产品：基础单价 + 采管费单价
        const baseUnitPrice = this.calculateBaseUnitPrice(item);
        const procurementFeeUnitPrice = this.calculateProcurementManagementFeeUnitPrice(item);
        return this.roundUnitPrice(baseUnitPrice + procurementFeeUnitPrice);
      } else {
        // 固定价产品：使用含税单价
        return item.itemTaxInclusiveUnitPrice || item.unitPrice || item.product_price || 0;
      }
    },
    
    // 计算结算项目的结算价（用于变动价产品，保留兼容性）
    calculateSettlementItemPrice(item) {
      if (item.itemPricingMethod === 'FLOATING') {
        // 变动价产品：使用结算单价（基础单价 + 采管费单价）
        return this.calculateSettlementUnitPrice(item);
      } else {
        // 固定价产品：使用含税单价
        return item.itemTaxInclusiveUnitPrice || item.unitPrice || item.product_price || 0;
      }
    },
    
    // 计算单个结算项目的含税金额
    getSettlementItemInclusiveAmount(item) {
      if (!item) return 0;
      
      // 固定价产品且有采管费时，使用实际单价计算含税金额
      if ((item.itemPricingMethod === 'FIXED' || !item.itemPricingMethod) && 
          this.hasProcurementManagementFee() && 
          (item.procurementManagementFee || 0) !== 0) {
        const actualUnitPrice = this.calculateActualUnitPrice(item); // 已经四舍五入到2位小数
        const quantity = Number(
          item.settledQuantity ??
          item.settlement_quantity ??
          item.receivedQuantity ??
          item.itemTotalQuantity ??
          0
        );
        const amount = actualUnitPrice * quantity;
        // 含税金额也四舍五入到2位小数
        return Math.round(amount * 100) / 100;
      }
      
      // 优先使用接口返回的含税金额字段
      if (item.totalAmount !== undefined && item.totalAmount !== null) {
        return Number(item.totalAmount) || 0;
      }
      if (item.product_tax_price !== undefined && item.product_tax_price !== null) {
        return Number(item.product_tax_price) || 0;
      }
      if (item.taxInclusiveTotalAmount !== undefined && item.taxInclusiveTotalAmount !== null) {
        return Number(item.taxInclusiveTotalAmount) || 0;
      }
      if (item.settledAmount !== undefined && item.settledAmount !== null) {
        return Number(item.settledAmount) || 0;
      }
      
      // 兜底：使用含税单价 * 结算数量
      const quantity = Number(
        item.settledQuantity ??
        item.settlement_quantity ??
        item.receivedQuantity ??
        item.itemTotalQuantity ??
        0
      );
      const unitPrice = this.calculateSettlementItemPrice(item);
      return quantity * unitPrice;
    },
    
    // 计算结算项目含税金额（用于表格显示，与getSettlementItemInclusiveAmount相同）
    calculateSettlementItemInclusiveAmount(item) {
      return this.getSettlementItemInclusiveAmount(item);
    },
    
    // 计算单个结算项目的税额
    calculateItemTaxAmount(item) {
      // 变动价产品：税额 = 含税结算金额 / (1 + 税率) * 税率
      if (item.itemPricingMethod === 'FLOATING') {
        // 获取含税结算金额
        const taxInclusiveAmount = item.settledAmount || item.price_total || item.totalAmount || item.product_tax_price || 0;
        // 获取税率
        const taxRate = item.taxRate || item.itemTaxRate || item.product_tax_rate || 0;
        // 税额 = 含税结算金额 / (1 + 税率) * 税率
        if (taxRate > 0) {
          return (Number(taxInclusiveAmount) / (1 + Number(taxRate))) * Number(taxRate);
        }
        return 0;
      } else {
        // 固定价产品：使用原来的计算方式
        // 获取含税单价（使用结算价计算方法，支持固定价和变动价）
        const taxInclusiveUnitPrice = this.calculateSettlementItemPrice(item);
        // 获取不含税单价
        const taxExclusiveUnitPrice = item.itemTaxExclusiveUnitPrice || 0;
        // 获取结算数量
        const quantity = Number(item.settledQuantity || item.settlement_quantity || 0);
        // 税额 = (含税单价 - 不含税单价) × 数量
        return (taxInclusiveUnitPrice - taxExclusiveUnitPrice) * quantity;
      }
    },
    
    // 计算不含税金额（含税金额 - 税额）
    calculateTaxExclusiveAmount(item) {
      // 获取含税金额（变动价产品使用含税结算金额，固定价产品使用含税金额）
      let taxInclusiveAmount = 0;
      if (item.itemPricingMethod === 'FLOATING') {
        // 变动价产品：使用含税结算金额
        taxInclusiveAmount = item.settledAmount || item.price_total || item.totalAmount || item.product_tax_price || 0;
      } else {
        // 固定价产品：使用含税金额
        taxInclusiveAmount = item.totalAmount || item.product_tax_price || item.settledAmount || item.price_total || 0;
      }
      // 获取税额
      const taxAmount = this.calculateItemTaxAmount(item);
      // 不含税金额 = 含税金额 - 税额
      return Number(taxInclusiveAmount) - taxAmount;
    },
    
    // 计算不含税单价（用于变动价产品）
    calculateTaxExclusiveUnitPrice(item) {
      if (item.itemPricingMethod === 'FLOATING') {
        // 变动价产品：尝试使用不含税固定价和不含税变动价
        const taxExclusiveBasePrice = item.itemTaxExclusiveBasePrice || 0;
        const taxExclusiveFloatingPrice = item.itemTaxExclusiveFloatingPrice || 0;
        
        // 如果有不含税固定价和不含税变动价，直接相加
        if (taxExclusiveBasePrice > 0 || taxExclusiveFloatingPrice > 0) {
          return taxExclusiveBasePrice + taxExclusiveFloatingPrice;
        }
        
        // 如果没有，则从含税单价计算：不含税单价 = 含税单价 - (含税单价 - 不含税单价)
        // 即：不含税单价 = 含税单价 - 单位税额
        const taxInclusiveUnitPrice = this.calculateSettlementItemPrice(item);
        const quantity = Number(item.settledQuantity || item.settlement_quantity || 1);
        const taxAmount = this.calculateItemTaxAmount(item);
        const unitTaxAmount = quantity > 0 ? taxAmount / quantity : 0;
        return taxInclusiveUnitPrice - unitTaxAmount;
      } else {
        // 固定价产品：直接使用不含税单价
        return item.itemTaxExclusiveUnitPrice || 0;
      }
    },
    
    // 计算采管费单价（采管费金额 / 结算数量）
    calculateProcurementManagementFeeUnitPrice(item) {
      // 固定价合计模式不在清单项计算采管费
      if (item.itemPricingMethod === 'FIXED' && 
          this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        return 0;
      }
      
      // 变动价合计模式不在清单项计算采管费
      if (item.itemPricingMethod === 'FLOATING' && 
          this.settlement.procurementManagementFeeMode === 'TOTAL_AMOUNT') {
        return 0;
      }
      
      // 获取采管费金额
      const procurementManagementFee = Number(item.procurementManagementFee || 0);
      // 获取结算数量
      const quantity = Number(item.settledQuantity || item.settlement_quantity || 0);
      
      // 如果数量为0，返回0
      if (quantity === 0) {
        return 0;
      }
      
      // 采管费单价 = 采管费金额 / 结算数量
      return this.roundUnitPrice(procurementManagementFee / quantity);
    },
    
    // 计算实际单价（含税单价 - 采管费单价，仅用于固定价产品）
    calculateActualUnitPrice(item) {
      // 只计算固定价产品的实际单价
      if (item.itemPricingMethod === 'FLOATING') {
        return 0;
      }
      
      if (item.actualUnitPrice !== undefined && item.actualUnitPrice !== null) {
        return this.roundUnitPrice(item.actualUnitPrice);
      }

      // 固定价产品：使用含税单价
      const taxInclusiveUnitPrice = item.itemTaxInclusiveUnitPrice || item.unitPrice || 0;
      const procurementFeeUnitPrice = this.calculateProcurementManagementFeeUnitPrice(item);
      return this.roundUnitPrice(taxInclusiveUnitPrice - procurementFeeUnitPrice);
    },
    
    // 计算固定价合计模式的采管费总额
    calculateFixedPriceTotalProcurementManagementFee() {
      if (!this.isFixedPriceProcurementManagementFee() || 
          !this.settlement.procurementManagementFeeMode) {
        return 0;
      }
      
      // 只有合计模式才计算总采管费
      if (this.settlement.procurementManagementFeeMode !== 'TOTAL_AMOUNT') {
        return 0;
      }
      
      if (this.settlement.procurementManagementFeeType === 'FIXED_UNIT_PRICE') {
        // 金额模式：直接使用用户输入的总金额
        return Number(this.settlement.procurementManagementFeeUnitPrice || 0);
      } else if (this.settlement.procurementManagementFeeType === 'FIXED_RATE') {
        // 比率模式：总金额 × 比率%
        const totalAmount = this.calculateSettlementItemsTotal();
        const rateDecimal = Number(this.settlement.procurementManagementFeeRate || 0);
        return totalAmount * rateDecimal;
      }
      
      return 0;
    },
    
    // 计算变动价合计模式的采管费总额
    calculateFloatingPriceTotalProcurementManagementFee() {
      if (!this.isFloatingPriceProcurementManagementFee() || 
          !this.settlement.procurementManagementFeeMode) {
        return 0;
      }
      
      // 只有合计模式才计算总采管费
      if (this.settlement.procurementManagementFeeMode !== 'TOTAL_AMOUNT') {
        return 0;
      }
      
      if (this.settlement.procurementManagementFeeType === 'FIXED_UNIT_PRICE') {
        // 金额模式：直接使用用户输入的总金额
        return Number(this.settlement.procurementManagementFeeUnitPrice || 0);
      } else if (this.settlement.procurementManagementFeeType === 'FIXED_RATE') {
        // 比率模式：总金额 × 比率%
        const totalAmount = this.calculateSettlementItemsTotal();
        const rateDecimal = Number(this.settlement.procurementManagementFeeRate || 0);
        return totalAmount * rateDecimal;
      }
      
      return 0;
    },
    
    // 获取采管费类型文本
    getProcurementManagementFeeTypeText() {
      if (!this.settlement.procurementManagementFeeType) {
        return '-';
      }
      
      // 如果是固定价产品，显示"比率模式"或"金额模式"
      if (this.isFixedPriceProcurementManagementFee()) {
        switch (this.settlement.procurementManagementFeeType) {
          case 'FIXED_RATE':
            return '比率模式';
          case 'FIXED_UNIT_PRICE':
            return '金额模式';
          default:
            return '-';
        }
      } else {
        // 如果是变动价产品，显示"固定比率"或"固定单价"
        switch (this.settlement.procurementManagementFeeType) {
          case 'FIXED_RATE':
            return '固定比率';
          case 'FIXED_UNIT_PRICE':
            return '固定单价';
          default:
            return '-';
        }
      }
    },
    
    // 格式化采管费比率显示（转换为百分比）
    formatProcurementManagementFeeRate() {
      const rate = this.settlement.procurementManagementFeeRate;
      if (rate === null || rate === undefined || rate === '') {
        return '-';
      }
      
      // 转换为百分比显示，保留4位小数
      const percentage = (Number(rate) * 100).toFixed(4);
      return `${percentage}%`;
    },
    
    // 获取采管费模式文本
    getProcurementManagementFeeModeText() {
      if (!this.settlement.procurementManagementFeeMode) {
        return '-';
      }
      
      switch (this.settlement.procurementManagementFeeMode) {
        case 'UNIT_PRICE':
          return '单价模式';
        case 'TOTAL_AMOUNT':
          return '合计模式';
        default:
          return '-';
      }
    },

  }
};
</script>

<style lang="scss" scoped>
.settlement-detail-container {
  padding: 20px;
  background-color: #fff;
  
  .page-header {
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .header-title {
      display: flex;
      align-items: center;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
    
    .header-actions {
      display: flex;
      gap: 10px;
    }
  }
  
  .loading-container {
    padding: 20px;
  }
  
  .settlement-info {
    margin-top: 20px;
    
    .section-title {
      margin-top: 25px;
      margin-bottom: 15px;
      padding-left: 10px;
      border-left: 4px solid #2c73eb;
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }
    
    .box-card {
      margin-bottom: 20px;
      
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .total-amount {
          font-size: 14px;
          color: #606266;
          display: flex;
          align-items: center;
          
          .amount-value {
            margin-left: 5px;
            font-size: 16px;
            font-weight: bold;
            color: #f56c6c;
          }
        }
      }
    }
    
    // 订单明细汇总样式
    .order-items-summary {
      margin-top: 15px;
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      padding: 10px 15px;
      background-color: #f9f9f9;
      border-top: 1px solid #ebeef5;
      border-radius: 0 0 4px 4px;
      
      .summary-item {
        margin-left: 20px;
        display: flex;
        align-items: center;
        
        .summary-label {
          font-size: 14px;
          color: #606266;
        }
        
        .summary-value {
          margin-left: 5px;
          font-weight: bold;
          color: #303133;
          
          &.highlight {
            .summary-label {
              color: #409EFF;
              font-weight: 500;
            }
            
            .summary-value {
              color: #f56c6c;
              font-size: 16px;
            }
          }
        }
      }
    }
    
    // 结算清单汇总样式
    .settlement-items-summary {
      margin-top: 15px;
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      padding: 10px 15px;
      background-color: #f9f9f9;
      border-top: 1px solid #ebeef5;
      border-radius: 0 0 4px 4px;
      
      .summary-item {
        margin-left: 20px;
        display: flex;
        align-items: center;
        
        .summary-label {
          font-size: 14px;
          color: #606266;
        }
        
        .summary-value {
          margin-left: 5px;
          font-weight: bold;
          color: #303133;
        }
        
        &.highlight {
          .summary-label {
            color: #409EFF;
            font-weight: 500;
          }
          
          .summary-value {
            color: #f56c6c;
            font-size: 16px;
          }
        }
      }
    }
    
    .settlement-items-section {
      margin-top: 20px;
    }
    
    .settlement-items-info {
      margin-bottom: 20px;
    }
    
    .el-table {
      margin-top: 10px;
      width: 100%;
      
      ::v-deep .el-table__header th {
        background-color: #f5f7fa;
        color: #606266;
      }
      
      ::v-deep .el-table__row {
        .cell {
          padding: 8px 12px;
        }
      }
      
      // 表格底部汇总行
      ::v-deep .el-table__footer {
        background-color: #f5f7fa;
        font-weight: bold;
        
        td {
          color: #303133;
        }
      }
      
      // 修复操作列样式
      ::v-deep .el-table__fixed-right {
        height: 100% !important;
        
        .el-table__fixed-header-wrapper {
          background-color: #f5f7fa;
        }
        
        .el-button--text {
          padding: 2px 0;
          margin: 0 5px;
        }
        
        .delete-button {
          color: #f56c6c;
        }
      }
    }
    
  }
  
  .action-buttons {
    margin-top: 30px;
    display: flex;
    justify-content: center;
    gap: 20px;
  }
}

// 打印样式
@media print {
  .hide-on-print {
    display: none !important;
  }
  
  .page-header, .action-buttons, .el-button {
    display: none !important;
  }
  
  .settlement-info {
    .box-card {
      box-shadow: none !important;
      border: 1px solid #ebeef5 !important;
      page-break-inside: avoid;
      
      .card-header {
        border-bottom: 1px solid #ebeef5 !important;
      }
    }
    
    .section-title {
      page-break-after: avoid;
    }
    
    .el-table {
      page-break-inside: auto;
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
    
    .settlement-items-summary {
      border: 1px solid #ebeef5;
      border-top: none;
    }
  }
}

.mb-15 {
  margin-bottom: 15px;
}

.section-divider {
  margin: 15px 0 10px 0;
  padding-left: 10px;
  border-left: 4px solid #2c73eb;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.empty-settlement-items {
  margin-top: 15px;
}

.orders-list {
  margin-bottom: 20px;
  
  .el-table {
    margin-top: 10px;
    
    &::before {
      display: none;
    }
    
    th {
      background-color: #f5f7fa;
      color: #606266;
      font-size: 13px;
      padding: 8px;
    }
    
    td {
      padding: 8px;
      font-size: 13px;
    }
  }
}

/* 打印专用区域样式 */
.print-only-area {
  display: none; /* 默认隐藏 */
  padding: 20px;
  background: white;
  font-family: "SimSun", "宋体", serif;
  
  /* 打印单位名称 */
  .print-unit-name {
    text-align: center;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 8px;
    color: #000;
  }
  
  /* 打印标题 */
  .print-title {
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
    color: #000;
  }
  
  /* 所有表格通用样式 */
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 0;
    font-size: 12px;
  }
  
  /* 结算期表格 */
  .print-period-table {
    border: 2px solid #000;
    
    tr {
      td {
        border: 1px solid #000;
        padding: 6px 4px;
        font-size: 12px;
        vertical-align: middle;
        
        &.period-label {
          width: 80px;
          text-align: center;
          font-weight: bold;
          background-color: #f0f0f0;
        }
        
        &.period-value {
          text-align: center;
          font-weight: normal;
        }
      }
    }
  }
  
  /* 单位信息表格 */
  .print-info-table {
    border: 2px solid #000;
    border-top: none;
    
    tr {
      td {
        border: 1px solid #000;
        padding: 6px 4px;
        font-size: 12px;
        vertical-align: middle;
        
        &.info-label {
          width: 80px;
          text-align: center;
          font-weight: bold;
          background-color: #f0f0f0;
        }
        
        &.info-value {
          text-align: left;
          padding-left: 8px;
          font-weight: normal;
        }
      }
    }
  }
  
  /* 材料明细表格 */
  .print-materials-table {
    border: 2px solid #000;
    border-top: none;
    
    thead {
      tr {
        th {
          border: 1px solid #000;
          padding: 8px 4px;
          font-size: 12px;
          font-weight: bold;
          text-align: center;
          vertical-align: middle;
          background-color: #f0f0f0;
          
          &.col-serial {
            width: 3%;
          }
          
          &.col-order {
            width: 6%;
          }
          
          &.col-name {
            width: 8%;
          }
          
          &.col-spec {
            width: 8%;
          }
          
          &.col-material {
            width: 6%;
          }
          
          &.col-unit {
            width: 4%;
          }
          
          &.col-quantity {
            width: 5%;
          }
          
          &.col-date {
            width: 6%;
          }
          
          &.col-price {
            width: 5%;
          }
          
          &.col-floating-price {
            width: 5%;
          }
          
          &.col-base-price {
            width: 5%;
          }
          
          &.col-amount {
            width: 6%;
          }
          
          &.col-tax-rate {
            width: 4%;
          }
          
          &.col-tax-amount {
            width: 5%;
          }
          
          &.col-remark {
            width: 5%;
          }
        }
      }
    }
    
    tbody {
      tr {
        td {
          border: 1px solid #000;
          padding: 8px 4px;
          font-size: 12px;
          vertical-align: middle;
          text-align: center;
        }
        
        &.total-row {
          font-weight: bold;
          background-color: #f8f8f8;
          
          td {
            text-align: center;
            vertical-align: middle;
          }
        }
        
        &.empty-row {
          td {
            height: 25px;
            border-bottom: 1px solid #000;
          }
        }
      }
    }
  }
  
  /* 签名区域表格 */
  .print-signature-table {
    border: none;
    table-layout: fixed;
    width: 100%;
    
    tr {
      td {
        border: none !important;
        padding: 20px 8px;
        font-size: 12px;
        vertical-align: top;
        width: 25%;
        height: 50px;
        text-align: left;
        
        .signature-label {
          font-weight: normal;
        }
        
        
        .signature-space {
          border-bottom: 1px solid #000;
          height: 20px;
          margin-top: 10px;
        }
      }
    }
  }
}

/* 打印区域在屏幕上也要保持一定的样式 */
.print-only-area table {
  font-family: "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
}

</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>
